'use strict';

import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import _ from 'lodash';
import parse from 'html-react-parser';
import {keys} from 'ts-transformer-keys';

import {Button} from 'primereact/button';
import {
    Checkbox,
    CheckboxChangeEvent
} from 'primereact/checkbox';
import {Dialog} from 'primereact/dialog';
import {DomHandler} from 'primereact/utils';
import {
    Dropdown,
    DropdownProps,
    DropdownChangeEvent,
    DropdownFilterEvent
} from 'primereact/dropdown';
import {
    MultiSelect,
    MultiSelectProps,
    MultiSelectChangeEvent
} from 'primereact/multiselect';
import {MenuItemCommandEvent} from 'primereact/menuitem';
import {Skeleton} from 'primereact/skeleton';
import {TieredMenu} from 'primereact/tieredmenu';
import {Toast} from 'primereact/toast';
import {VirtualScrollerLazyEvent} from 'primereact/virtualscroller';

import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import ReferenceSet from '~/shared/src/mm/ReferenceSet';
import Item from '~/shared/src/ci/Item';
import {
    ItemsAndReferencesCollection,
    ReferenceValue
} from '~/shared/src/utilities/dataUtils';
import {
    BaseRequestParameters,
    defaultBaseRequestParameters
} from '~/shared/src/utilities/modules/common';
import {
    ReferenceFieldLinkTarget,
    ReferenceFieldPresentation,
    LoadReferenceFieldItemsParameters,
    LoadReferenceFieldItemsResult,
    useHookFormDelay
} from '~/shared/src/utilities/modules/form';
import {Action} from '~/shared/src/utilities/action';
import {Query} from '~/shared/src/utilities/query';
import messageUtils from '~/shared/src/utilities/messageUtils';

import FieldWrapper, {
    defaultProps as fieldWrapperDefaultProps,
    componentProps as fieldWrapperComponentProps,
    ComponentProps as FieldWrapperComponentProps,
    DefaultProps as FieldWrapperDefaultProps,
    Props as FieldWrapperProps
} from '~/frontend/src/components/base/shared/field/FieldWrapper';
import Form, {ComponentProps as FormComponentProps} from '~/frontend/src/components/base/Form';
import GenericForm from '~/frontend/src/components/base/ItemFinder/detail/PropertiesPanel';
import LoadingMaskPanel from '~/frontend/src/components/base/LoadingMaskPanel';
import StaticContentField, {
    componentProps as staticContentFieldComponentProps,
    ComponentProps as StaticContentFieldComponentProps
} from '~/frontend/src/components/base/shared/field/StaticContentField';
import {getHeightStyle} from '~/frontend/src/components/base/shared/field/valueTypeFields/TextField';
import {getSubPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import {
    getItemTypeDisplayName,
    markFilterSearchesInText
} from '~/frontend/src/utilities/renderHelpers';
import {
    isInvalidHTML,
    statusColoringRenderer
} from '~/frontend/src/utilities/valueRenderers';
import {FormPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/definitions';
import {Variables} from '~/frontend/src/components/base/Form';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import xhr from '~/frontend/src/utilities/xhr';

import clearPanelProps from '~/frontend/src/actions/panelManager/clearPanelProps';
import openPanel from '~/frontend/src/actions/panelManager/openPanel';
import openPanelForItemType from '~/frontend/src/actions/panelManager/openPanelForItemType';
import {
    clearElusiveData,
    clearElusiveDataForSubpanels
} from '~/frontend/src/actions/elusiveData';
import {unsetMessage} from '~/frontend/src/actions/intercom';


type CommonInputPros = {
    [CP in keyof DropdownProps & keyof MultiSelectProps] : DropdownProps[CP] | MultiSelectProps[CP]
}
type InputProps = Omit<CommonInputPros, 'autoComplete' | 'onChange' | 'key'>;


type TransformedValue = {
    key : string;
    id : number;
    statusId : number;
    label : React.ReactNode;
    searchLabel? : React.ReactNode;
    sortLabel : string;
    itemId : number;
    item? : Item;
    additionalData? : ItemsAndReferencesCollection;
    isGroupHeader? : boolean;
    groupLabel? : string;
};
const transformedValueKeys = keys<TransformedValue>();

type OrderBy = {
    key : string;
    direction : 'asc' | 'desc';
};

type ValueVersions = {
    items : {
        [ id : number ] : number;
    };
    references : {
        [ id : number ] : number;
    };
};

export type GetDataConfig = {
    schema : Schema;
    itemTypeName : string;
    itemTypeNames : string[];

    searchString : string;
    orderBy? : string;

    searchTemplate : string;

    start : number;
    limit : number;

    filterItems : ( items : Item[] ) => Item[];
    orderItems : ( items : Item[] ) => Item[];
    pageItems : ( items : Item[] ) => Item[];
};

type DefaultProps = Pick<BaseRequestParameters, 'schema'> & FieldWrapperDefaultProps & {

    /**
     * Allow to add a new item to be referenced.
     */
    addNew? : boolean;

    /**
     * Allow to add a new item to be referenced.
     */
    addExisting? : boolean;

    /**
     * Item type names the "create new" menu is restricted to. Empty = every target item type of the
     * reference set(s). Needed where the reference set alone is not the whole truth - the key/value
     * field of an enum, for instance: which field types can edit that column follows from the bound
     * property's keyType/valueType ( valueTypeToFieldTypeMapping ), not from the reference set.
     */
    allowedItemTypes? : string[];

    /**
     * Show a delete button.
     */
    deleteButton? : boolean;

    /**
     * Is this field is disabled.
     */
    disabled? : boolean;

    /**
     * The number of text rows to be displayed within the field.
     * 0 means unlimited
     */
    height? : number;

    /**
     * Show a link button for the referenced item.
     */
    linkButton? : boolean;

    linkTarget? : ReferenceFieldLinkTarget;

    /**
     * The max number of text rows to be displayed within the field.
     * 0 means unlimited
     */
    maxHeight? : number;

    /**
     * Should the field marked if dirty.
     */
    markDirty? : boolean;

    presentation? : ReferenceFieldPresentation;

    /**
     * The number of columns to render the checkbox options with.
     * If set to 0 the number will be automatically computed to show all options in a row.
     *
     * Only applicable if <prop>presentation</prop> is set to 'checkboxes'.
     */
    presentationColumns? : number;

    /**
     * The tabIndex offset for forms opened within the input field.
     */
    tabIndexOffset? : number;

    /**
     * The CSS class names to style the input field with.
     */
    inputClassNames? : string[];

    /**
     * The styles to style the input field with.
     */
    inputStyle? : React.CSSProperties;

    /**
     * true to use react-hook-form with debounced onChange event
     */
    useHookForm? : boolean;

    /**
     * Is this field is used in a reference repeating container.
     */
    isRepeated? : boolean;

    editPanelConfigIds? : number[];
};

const defaultProps : Required<DefaultProps> = {
    schema: defaultBaseRequestParameters[ 'schema' ],

    ...fieldWrapperDefaultProps,

    addExisting        : true,
    addNew             : true,
    allowedItemTypes   : [],
    deleteButton       : true,
    disabled           : false,
    height             : 0,
    inputClassNames    : [],
    inputStyle         : {},
    linkButton         : true,
    linkTarget         : 'panel',
    markDirty          : false,
    maxHeight          : 0,
    presentation       : 'dropdown',
    presentationColumns: 1,
    tabIndexOffset     : 1,
    useHookForm        : false,
    isRepeated         : false,
    editPanelConfigIds : []
};

type CommonProps = Omit<FieldWrapperProps, 'displayValue' | 'pt'> & {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The uniqueId of the form the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    formUid : string;

    id? : string;

    /**
     * Restricts a reference set that allows `:n` to a single entry ( metamodel:
     * `LimitToSingleSelection` ) - this is what turns a polymorphic slot into one choice.
     * Unset the cardinality decides.
     */
    limitToSingleSelection? : boolean;

    /**
     * The value.
     */
    value? : ReferenceValue | ReferenceValue[];

    /**
     * The value to determine dirty against.
     */
    originalValue? : ReferenceValue | ReferenceValue[];

    /**
     * The dataContext to access global.data with.
     */
    dataContext : string;

    /**
     * The item for which the reference(s) is for.
     */
    rootItemId : number;

    /**
     * The direction of the reference(s) viewed from the rootItem.
     */
    referenceDirection : Omit<ReferenceDirection, 'Both'>;

    /**
     * The statusId for new entries.
     */
    newStatusId? : number;

    /**
     * The statusId for deleting entries.
     */
    deleteStatusId? : number;

    /**
     * The label to use for the items of this field.
     */
    itemLabel? : string;

    /**
     * The title to be used for the popup.
     */
    popupTitle? : string;

    /**
     * The panelConfig for open the reference target item in a panel or popup is filtered by this prefix.
     * If there is no panelConfig found this way the normal behavior takes place.
     */
    panelConfigFilterPrefix? : string;

    onChange : ( value : ReferenceValue | ReferenceValue[] | undefined, originalEvent : any ) => void;

    onIntermediateChange : ( value : ReferenceValue | ReferenceValue[] | undefined, originalEvent : any ) => void;

    onCreateNewItemForReference? : ( value : Item, originalEvent : any ) => Promise<void>;

    /**
     * The orderBy clause for ordering the content with.
     */
    orderBy? : string;

    /**
     * The query to load the references list with.
     */
    query? : Query;

    /**
     * The action to get the references list with.
     */
    getDataAction? : Action;

    onGetData? : ( getDataConfig : GetDataConfig ) => Promise<{
        items? : Item[];
        total : number
    } | undefined>;

    /**
     * Additional parameters for the query.
     *
     * NOTE: These are set by the parent component (e.g. Form) to hold some parameters
     * belonging to the context it is used in:
     *   - formParentItemId - the item id of a parent form, NOT the form this field is contained in
     *   - treeParentItemId - the item id of the next parent dynamic treeNodes rootItem
     *   - treeRootItemId   - the item id of the trees rootItem
     *
     * IMPORTANT: These parameters will be overridden by additionalParameters if set!
     */
    additionalQueryParameters? : Required<LoadReferenceFieldItemsParameters>[ 'query' ][ 'parameters' ];

    /**
     * Additional parameters for the query.
     *
     * NOTE: A mapping between query fields and form contents.
     *
     * USAGE:
     * DataStructure based query:
     *   - key: ${<DataObjectOfQuery.itemKey>.<attributePath>}; value: ${<DataObjectOfForm.itemKey>.<attributePath>}
     * Cypher or SQL based query:
     *   - key: ${PlaceholderAsWrittenInQuery}; value: ${<DataObjectOfForm.itemKey>.<attributePath>}
     */
    additionalParameters? : Required<LoadReferenceFieldItemsParameters>[ 'query' ][ 'parameters' ];

    /**
     * Additional parameters for the add item form.
     */
    additionalAddItemFormProps? : Partial<Omit<FormComponentProps, 'rootItemId' | 'rootItemKey' | 'rootItemType'>>;

    pt? : {
        fieldWrapper? : FieldWrapperProps[ 'pt' ];
    };

    variables? : Variables;
};
type Props = CommonProps & ( {
    /** The ReferenceSet ID for the reference(s) of this field. */
    referenceSetId : number;
    referenceType? : never;
} | {
    /** The ReferenceType Name for the reference(s) of this field. */
    referenceType : string;
    referenceSetId? : never;
} );


export type ComponentProps = InputProps & DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = InputProps & Required<DefaultProps> & Props & {
    onBlur : ( event : React.FocusEvent<HTMLInputElement> | React.FocusEvent<Element> ) => void;
    onFocus : ( event : React.FocusEvent<HTMLInputElement> | React.FocusEvent<Element> ) => void;
};


const ReferenceField = ( props : AllProps ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        const dispatch : AppDispatch                                  = useDispatch(),
              user                                                    = useSelector( ( state : StoreState ) => state.session?.user ),
              elusiveData                                             = useSelector( ( state : StoreState ) => state.elusiveData ),
              {
                  addExisting,
                  addNew,
                  additionalAddItemFormProps,
                  additionalParameters,
                  additionalQueryParameters,
                  appendToElementId,
                  autoCompleteDelay,
                  autoCompleteMinLength,
                  dataContext,
                  deleteButton,
                  deleteStatusId,
                  disabled,
                  editPanelConfigIds,
                  formUid,
                  getDataAction,
                  height,
                  id,
                  infoTextLayout,
                  inputClassNames,
                  inputStyle,
                  isRepeated,
                  itemLabel,
                  labelPosition,
                  labelVisibility,
                  linkButton,
                  linkTarget,
                  markDirty,
                  limitToSingleSelection,
                  maxHeight,
                  messagePlaceholders,
                  messageTemplateStringResolver,
                  newStatusId,
                  onBlur,
                  onChange,
                  onFocus,
                  onGetData,
                  onIntermediateChange,
                  orderBy,
                  originalValue,
                  allowedItemTypes,
                  panelConfigFilterPrefix,
                  placeholder,
                  popupTitle,
                  presentation,
                  presentationColumns,
                  pt,
                  query,
                  readOnly,
                  referenceDirection,
                  referenceSetId = 0,
                  referenceType,
                  rootItemId,
                  schema,
                  tabIndex,
                  tabIndexOffset,
                  tooltipDisabled,
                  uid,
                  useHookForm,
                  value,
                  variables
              }                                                       = props,
              // replace : by - to prevent errors caused by querySelector using this id
              elementId                                               = ( id ?? `${uid}_referenceField` ).replaceAll( ':', '-' ),
              editFormUid                                             = `${uid}-editItemDialogForm`,
              editFormDataContext                                     = `${editFormUid}-dataContext`,
              searchRequestUid                                        = 'loadData',
              editItemFormIntercomMessages                            = useSelector( ( state : StoreState ) => state.intercom[ editFormDataContext ] ),
              editFormProps                                           = useSelector( ( state : StoreState ) => getSubPanelProps( editFormUid, state ) ),
              currentValue                                            = useRef<ReferenceValue | ReferenceValue[] | undefined>( value ),
              dropdownComponentRef                                    = useRef<Dropdown>( null ),
              multiSelectComponentRef                                 = useRef<MultiSelect>( null ),
              newItemTypeMenuRef                                      = useRef<TieredMenu>( null ),
              filter                                                  = useRef<string>( '' ),
              editItem                                                = useRef<Item | null>( null ),
              filterTimeoutId                                         = useRef<NodeJS.Timeout | undefined>(),
              requestTimeoutId                                        = useRef<NodeJS.Timeout | undefined>(),
              first                                                   = useRef<number>( 0 ),
              last                                                    = useRef<number>( 0 ),
              timeoutId                                               = useRef<NodeJS.Timeout | undefined>(),
              fieldLeftTimeoutId                                      = useRef<NodeJS.Timeout | undefined>(),
              outValue                                                = useRef<ReferenceValue[] | undefined>(),
              triggerJustFocused                                      = useRef<boolean>( false ),
              toast                                                   = useRef<Toast>( null ),
              [ load, setLoad ]                                       = useState<boolean>( false ),
              [ searchItems, setSearchItems ]                         = useState<TransformedValue[]>( Array.from( { length: 101 } ) as TransformedValue[] ),
              [ searchItemsLoading, setSearchItemsLoading ]           = useState<boolean>( false ),
              [ showEditItemDialog, setShowEditItemDialog ]           = useState<boolean>( false ),
              [ forceRender, setForceRender ]                         = useState<number>( 0 ),
              [ editPanelConfig, setEditPanelConfig ]                 = useState<any>( {} ),
              [ appendTo, setAppendTo ]                               = useState<'self' | HTMLElement>( 'self' ),
              [ focus, setFocus ]                                     = useState<boolean>( false ),
              [ overlay, setOverlay ]                                 = useState<boolean>( false ),
              formElusiveData                                         = useMemo(
                  () => {
                      return elusiveData?.[ formUid ];
                  },
                  [
                      elusiveData,
                      formUid
                  ]
              ),
              editFormElusiveData                                     = useMemo(
                  () => {
                      return elusiveData?.[ editFormUid ];
                  },
                  [
                      elusiveData,
                      editFormUid
                  ]
              ),
              resolvedNewStatusId                                     = useMemo(
                  () => {
                      return newStatusId ?? metaModel[ schema ].getStatus( 'active' )!.id;
                  },
                  [
                      newStatusId
                  ]
              ),
              resolvedDeleteStatusId                                  = useMemo(
                  () => {
                      return deleteStatusId ?? metaModel[ schema ].getStatus( 'deleted' )!.id;
                  },
                  [
                      deleteStatusId
                  ]
              ),
              deletedStatusIds                                        = useMemo(
                  () => metaModel[ schema ].getDeletedStatus().map( s => s.id ),
                  [
                      schema
                  ]
              ),
              currentValueVersions                                    = useRef<ValueVersions>(),
              valueVersions                                           = useMemo(
                  () => {
                      const retVal : ValueVersions = { items: {}, references: {} };

                      if ( value && formElusiveData?.data?.items && formElusiveData?.data?.references ) {
                          let innerValue = value;

                          if ( !Array.isArray( innerValue ) ) {
                              innerValue = [ innerValue ];
                          }

                          for ( const entry of innerValue ) {
                              const referenceDeleted = global.data[ dataContext ]?.references[ entry.id ]?.isDeleted() ?? false;

                              if ( entry.srcItemId !== rootItemId && !retVal.items[ entry.srcItemId ] ) {
                                  if ( referenceDeleted && currentValueVersions.current?.items[ entry.srcItemId ] ) {
                                      retVal.items[ entry.srcItemId ] = currentValueVersions.current.items[ entry.srcItemId ];
                                  }
                                  else {
                                      retVal.items[ entry.srcItemId ] = formElusiveData.data.items[ entry.srcItemId ];
                                  }
                              }
                              if ( entry.destItemId !== rootItemId && !retVal.items[ entry.destItemId ] ) {
                                  if ( referenceDeleted && currentValueVersions.current?.items[ entry.destItemId ] ) {
                                      retVal.items[ entry.destItemId ] = currentValueVersions.current.items[ entry.destItemId ];
                                  }
                                  else {
                                      retVal.items[ entry.destItemId ] = formElusiveData.data.items[ entry.destItemId ];
                                  }
                              }
                              if ( !retVal.references[ entry.id ] ) {
                                  if ( referenceDeleted && currentValueVersions.current?.references[ entry.id ] ) {
                                      retVal.references[ entry.id ] = currentValueVersions.current.references[ entry.id ];
                                  }
                                  else {
                                      retVal.references[ entry.id ] = formElusiveData.data.references[ entry.id ];
                                  }
                              }
                          }
                      }

                      return retVal;
                  },
                  [
                      JSON.stringify( formElusiveData?.data ?? {} ),
                      rootItemId,
                      JSON.stringify( value )
                  ]
              ),
              heightStyle                                             = useMemo(
                  () => getHeightStyle( inputStyle, height, maxHeight, labelVisibility, labelPosition, infoTextLayout ),
                  [
                      height,
                      infoTextLayout,
                      inputStyle.height,
                      labelPosition,
                      labelVisibility,
                      maxHeight
                  ]
              ),
              rootItem                                                = useMemo(
                  () => {
                      return global.data[ dataContext ]?.items?.[ rootItemId ]
                  },
                  [
                      dataContext,
                      rootItemId
                  ]
              ),
              referenceSets                                           = useMemo(
                  () => {
                      const retVal : ReferenceSet[] = [];

                      if ( referenceSetId ) {
                          const referenceSet = metaModel[ schema ].getReferenceSet( referenceSetId );

                          if ( referenceSet ) {
                              retVal.push( referenceSet );
                          }
                      }
                      else if ( referenceType && rootItem ) {
                          const innerReferenceSets = referenceDirection === 'SrcToDest'
                              ? metaModel[ schema ].getReferenceSetsForItemTypesAndReferenceType( rootItem.itemTypeId, '*', referenceType, 'src' )
                              : metaModel[ schema ].getReferenceSetsForItemTypesAndReferenceType( rootItem.itemTypeId, '*', referenceType, 'dest' );

                          if ( innerReferenceSets.length ) {
                              retVal.push( ...innerReferenceSets );
                          }
                      }

                      return retVal;
                  },
                  [
                      dataContext,
                      referenceDirection,
                      referenceSetId,
                      referenceType,
                      rootItem,
                      schema
                  ]
              ),
              targetItemTypeIds                                       = useMemo(
                  () => {
                      const ids = [ ...new Set( referenceSets.map( rSet => referenceDirection === 'SrcToDest' ? rSet.destItemTypeId : rSet.srcItemTypeId ) ) ];

                      // The reference set says which item types may be linked at all; where the
                      // caller knows more ( the enum's key/value column accepts only the field types
                      // that can edit its value type ), it narrows the list further.
                      if ( !allowedItemTypes.length ) {
                          return ids;
                      }

                      return ids.filter( id => allowedItemTypes.includes( metaModel[ 'CFG' ].getItemType( id )?.name ?? '' ) );
                  },
                  [
                      allowedItemTypes,
                      referenceSets
                  ]
              ),
              linkable                                                = useMemo(
                  () => {
                      return linkButton && targetItemTypeIds.length > 0;
                  },
                  [
                      linkButton,
                      targetItemTypeIds
                  ]
              ),
              newAddable                                              = useMemo(
                  () => {
                      return addNew && ( !isRepeated || !value ) && targetItemTypeIds.length > 0;
                  },
                  [
                      addNew,
                      isRepeated,
                      targetItemTypeIds
                  ]
              ),
              existingAddable                                         = useMemo(
                  () => {
                      return addExisting && ( !isRepeated || !value ) && targetItemTypeIds.length > 0;
                  },
                  [
                      addExisting,
                      isRepeated,
                      targetItemTypeIds
                  ]
              ),
              multi                                                   = useMemo(
                  () => {
                      let retVal = false;

                      if ( !limitToSingleSelection && !isRepeated ) {
                          if ( referenceSets.length === 1 ) {
                              const cardinality = referenceSets[ 0 ].cardinality.split( ':' );

                              retVal = referenceDirection === 'SrcToDest'
                                  ? cardinality[ 1 ] !== '1'
                                  : cardinality[ 0 ] !== '1';
                          }
                          else if ( referenceSets.length ) {
                              retVal = true;
                          }
                      }

                      return retVal;
                  },
                  [
                      isRepeated,
                      limitToSingleSelection,
                      referenceDirection,
                      referenceSets
                  ]
              ),
              messageResolver                                         = useMemo(
                  () => {
                      const mR = rootItem
                          ? messageUtils.getMessageTemplateStringResolver(
                              rootItem,
                              referenceSets.length === 1
                                  ? referenceSets[ 0 ]
                                  : undefined,
                              messagePlaceholders
                          )
                          : undefined;

                      return ( messageKey : string ) => messageKey === ''
                          ? undefined
                          : mR
                              ? mR( __( messageKey, messagePlaceholders ) )
                              : __( messageKey, messagePlaceholders );
                  },
                  [
                      dataContext,
                      JSON.stringify( messagePlaceholders ),
                      messageTemplateStringResolver,
                      referenceSets,
                      rootItem
                  ]
              ),
              resolvedPaceholder                                      = useMemo(
                  () => {
                      return placeholder ? messageResolver( placeholder ) : undefined;
                  },
                  [
                      JSON.stringify( placeholder ),
                      messageResolver,
                      user ? user.language : undefined
                  ]
              ),
              queryParameters                                         = useMemo(
                  () => {
                      let retVal : Required<LoadReferenceFieldItemsParameters>[ 'query' ][ 'parameters' ] = {};

                      if ( query?.type === 'DataStructure' ) {
                          retVal = {
                              ...additionalQueryParameters,
                              ...additionalParameters
                          };
                      }

                      // filter to parameters the query knows (placeholders contained in the query string)
                      else {
                          const additionalParametersToProcess     = {
                                    ...additionalQueryParameters,
                                    ...additionalParameters
                                },
                                additionalParametersToProcessKeys = Object.keys( additionalParametersToProcess ?? {} );

                          if ( query?.parameters?.length ) {
                              for ( const parameter of query.parameters ) {
                                  const parameterTemplate = `\$\{${parameter}\}`,
                                        parameterValue    = messageTemplateStringResolver?.( parameterTemplate, true );

                                  if ( parameterValue !== undefined && parameterValue !== parameterTemplate ) {
                                      retVal[ parameter ] = parameterValue;
                                  }
                                  else if ( additionalParametersToProcessKeys.includes( parameter ) ) {
                                      retVal[ parameter ] = additionalParametersToProcess[ parameter ];
                                  }
                              }
                          }
                      }

                      return retVal;
                  },
                  [
                      additionalParameters,
                      additionalQueryParameters,
                      messageTemplateStringResolver,
                      query
                  ]
              ),
              sortTransformedValue                                    = useCallback(
                  ( transformedValue : TransformedValue[] ) : TransformedValue[] => {
                      const orderByDefinitions : OrderBy[] = [],
                            sortFnc                        = ( value1 : TransformedValue, value2 : TransformedValue, orderByDefinition : OrderBy ) : -1 | 0 | 1 => {
                                let orderValue1 = value1.item
                                        ? messageUtils.getMessageTemplateStringResolver( value1.item )( `\${${orderByDefinition.key}}`, true ) ?? ''
                                        : '',
                                    orderValue2 = value2.item
                                        ? messageUtils.getMessageTemplateStringResolver( value2.item )( `\${${orderByDefinition.key}}`, true ) ?? ''
                                        : '';

                                // order strings case independent
                                if ( orderValue1 && typeof orderValue1 === 'string' ) {
                                    orderValue1 = orderValue1.toLowerCase();
                                }
                                if ( orderValue2 && typeof orderValue2 === 'string' ) {
                                    orderValue2 = orderValue2.toLowerCase();
                                }

                                if ( orderValue1 > orderValue2 ) {
                                    return orderByDefinition.direction === 'asc' ? 1 : -1;
                                }
                                else if ( orderValue1 < orderValue2 ) {
                                    return orderByDefinition.direction === 'asc' ? -1 : 1;
                                }
                                else {
                                    return 0;
                                }
                            };

                      const retVal = transformedValue;

                      if ( orderBy ) {
                          const orderByParts = orderBy.split( ',' );

                          for ( const orderByPart of orderByParts ) {
                              const orderByPartParts = ( orderByPart.trim().replaceAll( /\s+/g, ' ' ) ).split( ' ' );

                              if ( orderByPartParts.length && orderByPartParts.length < 3 ) {
                                  const direction = ( orderByPartParts[ 1 ] ?? 'asc' ).toLowerCase();

                                  if ( direction === 'asc' || direction === 'desc' ) {
                                      orderByDefinitions.push( {
                                          key: orderByPartParts[ 0 ],
                                          direction
                                      } );
                                  }
                              }
                          }
                      }

                      // sort by order by
                      if ( orderByDefinitions.length ) {
                          retVal.sort( ( value1, value2 ) => {
                              let innerRetVal : number,
                                  orderByCount : number = 0;

                              do {
                                  innerRetVal = sortFnc( value1, value2, orderByDefinitions[ orderByCount++ ] );
                              } while ( innerRetVal === 0 && orderByCount < orderByDefinitions.length )

                              return innerRetVal;
                          } );
                      }
                      // sort by label
                      else {
                          retVal.sort( ( value1, value2 ) => {
                              if ( value1.sortLabel.toLowerCase() > value2.sortLabel.toLowerCase() ) {
                                  return 1;
                              }
                              else if ( value1.sortLabel.toLowerCase() < value2.sortLabel.toLowerCase() ) {
                                  return -1;
                              }
                              else {
                                  return 0;
                              }
                          } );
                      }

                      return retVal;
                  },
                  [
                      messageTemplateStringResolver,
                      orderBy
                  ]
              ),
              transformedValue                                        = useMemo(
                  () => {
                      const retVal : TransformedValue[] = [];

                      if ( value && referenceSets.length ) {
                          let tValue : ReferenceValue[];

                          if ( !Array.isArray( value ) ) {
                              tValue = [ value ];
                          }
                          else {
                              tValue = value;
                          }

                          for ( const entry of tValue ) {
                              if ( !deletedStatusIds.includes( entry.statusId ) ) {
                                  const reference = global.data[ dataContext ].references[ entry.id ];

                                  let item = entry.item;

                                  if ( !item ) {
                                      const itemId = entry[ referenceDirection === 'SrcToDest' ? 'destItemId' : 'srcItemId' ];

                                      item = global.data[ dataContext ].items[ itemId ];
                                  }

                                  if ( item ) {
                                      let label : string;

                                      if ( itemLabel ) {
                                          let resolvedReferenceSet : ReferenceSet | undefined;

                                          if ( referenceSets.length === 1 ) {
                                              resolvedReferenceSet = referenceSets[ 0 ];
                                          }
                                          else if ( referenceSets.length ) {
                                              resolvedReferenceSet = referenceSets[ targetItemTypeIds.indexOf( item.itemTypeId ) ];
                                          }

                                          const labelMessageResolver = messageUtils.getMessageTemplateStringResolver( item, reference ?? resolvedReferenceSet, messagePlaceholders );

                                          label = labelMessageResolver( __( itemLabel, messagePlaceholders ) );
                                      }
                                      else {
                                          label = item.displayAttributeValue;

                                          if ( !label ) {
                                              label = item.itemType.displayAttribute === 'key'
                                                  ? item.name
                                                  : item.key;
                                          }
                                      }

                                      retVal.push( {
                                          ..._.pick( entry, transformedValueKeys ) as TransformedValue,
                                          key      : String( entry.id ),
                                          label    : statusColoringRenderer(
                                              parse( label ),
                                              item.statusId,
                                              schema,
                                              `item_${elementId}_${entry.id}`,
                                              `item_${elementId}_${entry.id}`
                                          ),
                                          sortLabel: label.toLowerCase(),
                                          itemId   : item.id,
                                          item     : item
                                      } );
                                  }
                              }
                          }
                      }

                      return sortTransformedValue( retVal );
                  },
                  [
                      dataContext,
                      elementId,
                      itemLabel,
                      JSON.stringify( messagePlaceholders ),
                      messageTemplateStringResolver,
                      orderBy,
                      referenceDirection,
                      referenceSets,
                      schema,
                      sortTransformedValue,
                      targetItemTypeIds,
                      user ? user.language : undefined,
                      JSON.stringify( value ),
                      JSON.stringify( valueVersions )
                  ]
              ),
              [ elementValue, setElementValue ]                       = useState<TransformedValue[]>( transformedValue ),
              currentElementValue                                     = useRef<TransformedValue[]>( transformedValue ),
              currentTransformedValue                                 = useRef<TransformedValue[]>( transformedValue ),
              buildUpdateAndSetElementValue                           = useCallback(
                  ( removedTransformedValues : TransformedValue[], addedTransformedValues : TransformedValue[], updatedTransformedValues : TransformedValue[] = [] ) : ReferenceValue[] | undefined => {
                      const newElementValue                           = [ ...currentElementValue.current ],
                            updatedElementValues : TransformedValue[] = [];

                      let retVal : ReferenceValue[] | undefined = [];

                      // remove
                      for ( const removedTransformedValue of removedTransformedValues ) {
                          const removedElementValueIndex = newElementValue.findIndex( nEV => nEV.id === removedTransformedValue.id );

                          if ( removedElementValueIndex !== -1 ) {
                              newElementValue.splice( removedElementValueIndex, 1 );
                          }
                      }

                      // add
                      for ( const addedTransformedValue of addedTransformedValues ) {
                          if ( addedTransformedValue.item ) {
                              let count = 1,
                                  newKey : string,
                                  label : string;

                              do {
                                  newKey = `NEW_${count++}`;
                              }
                              while ( newElementValue.find( nEV => nEV.key === newKey ) );

                              if ( itemLabel ) {
                                  let resolvedReferenceSet : ReferenceSet | undefined;

                                  if ( referenceSets.length === 1 ) {
                                      resolvedReferenceSet = referenceSets[ 0 ];
                                  }
                                  else if ( referenceSets.length ) {
                                      resolvedReferenceSet = referenceSets[ targetItemTypeIds.indexOf( addedTransformedValue.item.itemTypeId ) ];
                                  }

                                  const labelMessageResolver = messageUtils.getMessageTemplateStringResolver( addedTransformedValue.item, resolvedReferenceSet, messagePlaceholders );

                                  label = labelMessageResolver( __( itemLabel, messagePlaceholders ) );
                              }
                              else {
                                  label = addedTransformedValue.item.displayAttributeValue;

                                  if ( !label ) {
                                      label = addedTransformedValue.item.itemType.displayAttribute === 'key'
                                          ? addedTransformedValue.item.name
                                          : addedTransformedValue.item.key;
                                  }
                              }

                              newElementValue.push( {
                                  id            : 0,
                                  key           : newKey,
                                  statusId      : resolvedNewStatusId,
                                  label         : statusColoringRenderer(
                                      parse( label ),
                                      addedTransformedValue.item.statusId,
                                      schema,
                                      `item_${elementId}_${newKey}`,
                                      `item_${elementId}_${newKey}`
                                  ),
                                  sortLabel     : label.toLowerCase(),
                                  itemId        : addedTransformedValue.item.id,
                                  item          : addedTransformedValue.item,
                                  additionalData: addedTransformedValue.additionalData
                              } );
                          }
                      }

                      // update
                      for ( const updatedTransformedValue of updatedTransformedValues ) {
                          if ( updatedTransformedValue.item ) {
                              const existingTransformedValue = newElementValue.find( nEV => nEV.itemId === updatedTransformedValue.itemId );

                              if ( existingTransformedValue ) {
                                  let label : string;

                                  if ( itemLabel ) {
                                      let resolvedReferenceSet : ReferenceSet | undefined;

                                      if ( referenceSets.length === 1 ) {
                                          resolvedReferenceSet = referenceSets[ 0 ];
                                      }
                                      else if ( referenceSets.length ) {
                                          resolvedReferenceSet = referenceSets[ targetItemTypeIds.indexOf( updatedTransformedValue.item.itemTypeId ) ];
                                      }

                                      const labelMessageResolver = messageUtils.getMessageTemplateStringResolver( updatedTransformedValue.item, resolvedReferenceSet, messagePlaceholders );

                                      label = labelMessageResolver( __( itemLabel, messagePlaceholders ) );
                                  }
                                  else {
                                      label = updatedTransformedValue.item.displayAttributeValue;

                                      if ( !label ) {
                                          label = updatedTransformedValue.item.itemType.displayAttribute === 'key'
                                              ? updatedTransformedValue.item.name
                                              : updatedTransformedValue.item.key;
                                      }
                                  }

                                  existingTransformedValue.label = statusColoringRenderer(
                                      parse( label ),
                                      updatedTransformedValue.item.statusId,
                                      schema,
                                      `item_${elementId}_${existingTransformedValue.id}`,
                                      `item_${elementId}_${existingTransformedValue.id}`
                                  );
                                  existingTransformedValue.sortLabel = label.toLowerCase();
                                  existingTransformedValue.item = updatedTransformedValue.item;
                                  existingTransformedValue.additionalData = updatedTransformedValue.additionalData;

                                  // not an update of a new entry
                                  if ( !existingTransformedValue.key.match( /^NEW_\d*$/ ) ) {
                                      updatedElementValues.push( existingTransformedValue );
                                  }
                              }
                          }
                      }

                      sortTransformedValue( newElementValue );
                      setElementValue( newElementValue );

                      // get update value
                      // delete
                      for ( const transformedValue1 of currentTransformedValue.current ) {
                          if ( !newElementValue.find( nEV => nEV.id === transformedValue1.id ) ) {
                              const valueToUpdate = ( Array.isArray( currentValue.current ) ? currentValue.current : [ currentValue.current ] ).find( v => v?.id === transformedValue1.id );

                              if ( valueToUpdate && resolvedDeleteStatusId ) {
                                  retVal.push( {
                                      ...valueToUpdate,
                                      statusId: resolvedDeleteStatusId
                                  } );
                              }
                          }
                      }
                      // add
                      for ( const transformedValue1 of newElementValue ) {
                          if ( transformedValue1.item && transformedValue1.key.match( /^NEW_\d*$/ ) && rootItem ) {
                              let resolvedReferenceSet : ReferenceSet | undefined;

                              if ( referenceSets.length === 1 ) {
                                  resolvedReferenceSet = referenceSets[ 0 ];
                              }
                              else if ( referenceSets.length ) {
                                  resolvedReferenceSet = referenceSets[ targetItemTypeIds.indexOf( transformedValue1.item.itemTypeId ) ];
                              }

                              if ( resolvedReferenceSet ) {
                                  retVal.push( {
                                      id            : 0,
                                      referenceSetId: resolvedReferenceSet.id,
                                      srcItemId     : referenceDirection === 'SrcToDest' ? rootItemId : transformedValue1.item.id,
                                      destItemId    : referenceDirection === 'SrcToDest' ? transformedValue1.item.id : rootItemId,
                                      statusId      : resolvedNewStatusId,
                                      item          : transformedValue1.item,
                                      additionalData: transformedValue1.additionalData
                                  } );
                              }
                          }
                      }
                      // update
                      for ( const transformedValue1 of updatedElementValues ) {
                          const valueToUpdate = ( Array.isArray( currentValue.current ) ? currentValue.current : [ currentValue.current ] ).find( v => v?.id === transformedValue1.id );

                          if ( valueToUpdate && resolvedDeleteStatusId ) {
                              retVal.push( {
                                  ...valueToUpdate,
                                  item          : transformedValue1.item,
                                  additionalData: transformedValue1.additionalData
                              } );
                          }
                      }

                      if ( !retVal.length ) {
                          retVal = undefined;
                      }

                      outValue.current = retVal;

                      return retVal;
                  },
                  [
                      dataContext,
                      itemLabel,
                      referenceDirection,
                      referenceSets,
                      resolvedDeleteStatusId,
                      resolvedNewStatusId,
                      rootItemId,
                      schema,
                      targetItemTypeIds
                  ]
              ),
              emitOnChange                                            = useCallback(
                  ( event : React.FocusEvent<HTMLInputElement> | React.FocusEvent<Element> | React.KeyboardEvent<HTMLElement> | React.SyntheticEvent | undefined ) => {
                      if ( outValue.current ) {
                          onChange( outValue.current, event );
                          outValue.current = undefined;
                      }
                  },
                  [
                      onChange
                  ]
              ),
              onFieldLeft                                             = useCallback(
                  ( event : React.FocusEvent<HTMLInputElement> | React.FocusEvent<Element> | React.KeyboardEvent<HTMLElement> | React.SyntheticEvent | undefined ) => {
                      const overlayElement = dropdownComponentRef.current?.getOverlay() ?? multiSelectComponentRef.current?.getOverlay();

                      if ( overlayElement ) {
                          const activeElement = document.activeElement;

                          if ( overlayElement !== activeElement && !overlayElement.contains( activeElement ) ) {
                              if ( dropdownComponentRef.current?.getOverlay() ) {
                                  dropdownComponentRef.current.hide();
                              }
                              else if ( multiSelectComponentRef.current?.getOverlay() ) {
                                  multiSelectComponentRef.current.hide();
                              }
                          }
                      }
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onInputFocus                                            = useCallback(
                  ( event : React.FocusEvent<HTMLInputElement> | React.FocusEvent<Element> ) => {
                      setFocus( true );

                      if ( fieldLeftTimeoutId.current ) {
                          clearTimeout( fieldLeftTimeoutId.current );
                      }

                      onFocus?.( event );
                  },
                  [
                      onFocus
                  ]
              ),
              onInputBlur                                             = useCallback(
                  ( event : React.FocusEvent<HTMLInputElement> | React.FocusEvent<Element> ) => {
                      if ( !triggerJustFocused.current ) {
                          setFocus( false );

                          if ( timeoutId.current ) {
                              clearTimeout( timeoutId.current );
                          }

                          if ( fieldLeftTimeoutId.current ) {
                              clearTimeout( fieldLeftTimeoutId.current );
                          }
                          fieldLeftTimeoutId.current = setTimeout(
                              onFieldLeft.bind( null, event ),
                              35
                          );

                          emitOnChange( event );

                          onBlur?.( event );
                      }
                      else {
                          triggerJustFocused.current = false;
                      }
                  },
                  [
                      onBlur,
                      onFieldLeft,
                      emitOnChange
                  ]
              ),
              onButtonFocus                                           = useCallback(
                  ( event : React.FocusEvent<Element> ) => {
                      triggerJustFocused.current = true;
                      onInputFocus( event );
                  },
                  [
                      onInputFocus
                  ]
              ),
              onButtonBlur                                            = useCallback(
                  ( event : React.FocusEvent<Element> ) => {
                      triggerJustFocused.current = false;
                      onInputBlur( event );
                  },
                  [
                      onInputBlur
                  ]
              ),
              onTriggerFocus                                          = useCallback(
                  ( element : HTMLElement, event : React.FocusEvent<HTMLElement> ) => {
                      triggerJustFocused.current = true;
                      onInputFocus( event );
                  },
                  [
                      onInputFocus
                  ]
              ),
              onTriggerBlur                                           = useCallback(
                  ( element : HTMLElement, event : React.FocusEvent<HTMLElement> ) => {
                      triggerJustFocused.current = false;
                      onInputBlur( event );
                  },
                  [
                      onInputBlur
                  ]
              ),
              onDocumentKeyDown                                       = useCallback(
                  ( event : React.KeyboardEvent<HTMLElement> ) => {
                      let handled : boolean = false,
                          component : Dropdown | MultiSelect | undefined,
                          element : HTMLElement | undefined,
                          overlayElement : HTMLElement | undefined,
                          clearTrigger : HTMLElement | undefined,
                          downTrigger : HTMLElement | undefined;

                      switch ( event.key ) {

                          case 'Enter':
                              if ( !multi && dropdownComponentRef.current ) {
                                  component = dropdownComponentRef.current;
                                  element = component.getElement();
                                  overlayElement = component.getOverlay();
                                  clearTrigger = ( element?.querySelector( 'i.p-dropdown-clear-icon' ) ?? element?.querySelector( 'svg.p-dropdown-clear-icon' ) ) as HTMLElement | undefined;
                                  downTrigger = element?.querySelector( 'div.p-dropdown-trigger' ) as HTMLElement | undefined;
                              }
                              else if ( multi && multiSelectComponentRef.current ) {
                                  component = multiSelectComponentRef.current;
                                  element = component.getElement();
                                  overlayElement = component.getOverlay();
                                  clearTrigger = ( element?.querySelector( 'i.p-multiselect-clear-icon' ) ?? element?.querySelector( 'svg.p-dropdown-clear-icon' ) ) as HTMLElement | undefined;
                                  downTrigger = element?.querySelector( 'div.p-multiselect-trigger' ) as HTMLElement | undefined;
                              }

                              if ( event.target === clearTrigger ) {
                                  const newValue = buildUpdateAndSetElementValue( !multi ? [ currentElementValue.current[ 0 ] ] : currentElementValue.current, [] );

                                  handled = true;

                                  if ( newValue ) {
                                      if ( !useHookForm ) {
                                          emitOnChange( event );
                                      }
                                      else {
                                          onIntermediateChange?.( newValue, event );

                                          if ( timeoutId.current ) {
                                              clearTimeout( timeoutId.current );
                                          }
                                          timeoutId.current = setTimeout( emitOnChange.bind( null, event ), useHookFormDelay );
                                      }
                                  }
                              }
                              else if ( event.target === downTrigger ) {
                                  handled = true;

                                  if ( overlayElement ) {
                                      // missing TypeScript definition for Dropdown.hide
                                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                      // @ts-ignore
                                      component!.hide();
                                  }
                                  else {
                                      // missing TypeScript definition for Dropdown.show
                                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                      // @ts-ignore
                                      component!.show();
                                  }
                              }

                              if ( handled ) {
                                  event.stopPropagation();
                                  event.preventDefault();
                              }

                              break;

                          case 'Escape':
                              if ( dropdownComponentRef.current?.getOverlay() ) {
                                  dropdownComponentRef.current.hide();
                              }
                              else if ( multiSelectComponentRef.current?.getOverlay() ) {
                                  multiSelectComponentRef.current.hide();
                              }

                              break;
                      }
                  },
                  [
                      buildUpdateAndSetElementValue,
                      emitOnChange,
                      multi,
                      onIntermediateChange,
                      referenceDirection,
                      referenceSetId,
                      resolvedDeleteStatusId,
                      rootItemId,
                      schema,
                      useHookForm
                  ]
              ),
              onDropdownChange                                        = useCallback(
                  ( event : DropdownChangeEvent ) => {
                      // ignore clicks on a faked group header row (polymorphic '*' binding)
                      if ( ( event.value as TransformedValue )?.isGroupHeader ) {
                          return;
                      }

                      const dropdownValue            = event.value as TransformedValue,
                            removedTransformedValues = currentElementValue.current[ 0 ] ? [ currentElementValue.current[ 0 ] ] : [],
                            newTransformedValues     = dropdownValue ? [ dropdownValue ] : [],
                            newValue                 = buildUpdateAndSetElementValue( removedTransformedValues, newTransformedValues );

                      if ( newValue ) {
                          // even on clear
                          if ( !useHookForm || !newTransformedValues.length ) {
                              emitOnChange( event.originalEvent );
                          }
                          else {
                              onIntermediateChange?.( newValue, event.originalEvent );

                              if ( timeoutId.current ) {
                                  clearTimeout( timeoutId.current );
                              }
                              timeoutId.current = setTimeout( emitOnChange.bind( null, event.originalEvent ), 2 * useHookFormDelay );
                          }
                      }
                  },
                  [
                      buildUpdateAndSetElementValue,
                      emitOnChange,
                      onIntermediateChange,
                      referenceDirection,
                      referenceSetId,
                      resolvedDeleteStatusId,
                      rootItemId,
                      schema,
                      useHookForm
                  ]
              ),
              onDropdownKeyDown                                       = useCallback(
                  ( event : React.KeyboardEvent<HTMLDivElement> ) => {
                      if ( !existingAddable && dropdownComponentRef.current ) {
                          switch ( event.key ) {
                              // down
                              case 'ArrowDown':
                              // enter
                              case 'Enter':
                              // space
                              case ' ':
                                  dropdownComponentRef.current.hide();
                                  event.stopPropagation();
                                  event.preventDefault();
                          }
                      }
                  },
                  [
                      existingAddable
                  ]
              ),
              onDropdownClick                                         = useCallback(
                  ( event : MouseEvent ) => {
                      if ( !existingAddable && dropdownComponentRef.current ) {
                          // missing TypeScript definition for Dropdown.hide
                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                          // @ts-ignore
                          dropdownComponentRef.current.hide();
                          DomHandler.focus( dropdownComponentRef.current.getInput() );

                          event.stopPropagation();
                          event.preventDefault();
                      }
                  },
                  [
                      existingAddable
                  ]
              ),
              onDropdownShow                                          = useCallback(
                  () => {
                      if ( fieldLeftTimeoutId.current ) {
                          clearTimeout( fieldLeftTimeoutId.current );
                      }

                      setOverlay( true );
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onMultiSelectShow                                       = useCallback(
                  () => {
                      if ( fieldLeftTimeoutId.current ) {
                          clearTimeout( fieldLeftTimeoutId.current );
                      }

                      setOverlay( true );

                      if ( multiSelectComponentRef.current ) {
                          const el = document.getElementsByClassName( 'p-inputtext p-multiselect-filter' )[ 0 ] as HTMLElement;

                          if ( el && document.activeElement !== el ) {
                              el.focus( {
                                  preventScroll: false
                              } );
                          }
                      }
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onMultiSelectChange                                     = useCallback(
                  ( event : MultiSelectChangeEvent ) => {
                      // drop any faked group header rows (polymorphic '*' binding) from the selection
                      const multiSelectValue         = ( ( event.value ?? [] ) as TransformedValue[] ).filter( mSV => !mSV?.isGroupHeader ),
                            removedTransformedValues = currentElementValue.current.filter( tV => !multiSelectValue.find( mSV => mSV.itemId === tV.itemId ) ),
                            newTransformedValues     = multiSelectValue.filter( mSV => !currentElementValue.current.find( tV => tV.itemId === mSV.itemId ) ),
                            newValue                 = buildUpdateAndSetElementValue( removedTransformedValues, newTransformedValues );

                      if ( newValue ) {
                          if ( !useHookForm ) {
                              emitOnChange( event.originalEvent );
                          }
                          else {
                              onIntermediateChange?.( newValue, event.originalEvent );

                              if ( timeoutId.current ) {
                                  clearTimeout( timeoutId.current );
                              }
                              timeoutId.current = setTimeout( emitOnChange.bind( null, event.originalEvent ), 2 * useHookFormDelay );
                          }

                          event.stopPropagation();
                          event.preventDefault();
                          event.originalEvent.stopPropagation();
                          event.originalEvent.preventDefault();
                      }
                  },
                  [
                      buildUpdateAndSetElementValue,
                      emitOnChange,
                      onIntermediateChange,
                      referenceDirection,
                      referenceSetId,
                      resolvedDeleteStatusId,
                      rootItemId,
                      schema,
                      useHookForm
                  ]
              ),
              onMultiSelectRemove                                     = useCallback(
                  ( itemTransformedValue : TransformedValue, originalEvent : any ) => {
                      const newValue = buildUpdateAndSetElementValue( [ itemTransformedValue ], [] );

                      if ( newValue ) {
                          if ( !useHookForm ) {
                              emitOnChange( originalEvent );
                          }
                          else {
                              onIntermediateChange?.( newValue, originalEvent );

                              if ( timeoutId.current ) {
                                  clearTimeout( timeoutId.current );
                              }
                              timeoutId.current = setTimeout( emitOnChange.bind( null, originalEvent ), 2 * useHookFormDelay );
                          }
                      }
                  },
                  [
                      buildUpdateAndSetElementValue,
                      emitOnChange,
                      onIntermediateChange,
                      referenceDirection,
                      referenceSetId,
                      resolvedDeleteStatusId,
                      rootItemId,
                      schema,
                      useHookForm
                  ]
              ),
              onMultiselectKeyDown                                    = useCallback(
                  ( event : React.KeyboardEvent<HTMLDivElement> ) => {
                      if ( !existingAddable && multiSelectComponentRef.current ) {
                          switch ( event.key ) {
                              // down
                              case 'ArrowDown':
                              // space
                              case ' ':
                                  multiSelectComponentRef.current.hide();
                                  event.stopPropagation();
                                  event.preventDefault();
                          }
                      }
                  },
                  [
                      existingAddable
                  ]
              ),
              onMultiselectClick                                      = useCallback(
                  ( event : MouseEvent ) => {
                      if (
                          !existingAddable
                          && multiSelectComponentRef.current
                          && (
                              !event.target
                              || (
                                  !( event.target as HTMLElement ).classList.contains( 'p-multiselect-token-icon' )
                                  && !( event.target as HTMLElement ).classList.contains( 'p-multiselect-clear-icon' )
                              )
                          )
                      ) {
                          multiSelectComponentRef.current.hide();
                          DomHandler.focus( multiSelectComponentRef.current.getInput() );

                          event.stopPropagation();
                          event.preventDefault();
                      }
                  },
                  [
                      existingAddable
                  ]
              ),
              onCheckboxChange                                        = useCallback(
                  ( checkboxValue : TransformedValue, event : CheckboxChangeEvent ) => {
                      // ignore a faked group header row (polymorphic '*' binding)
                      if ( checkboxValue?.isGroupHeader ) {
                          return;
                      }

                      const removedTransformedValues = !event.checked ? [ checkboxValue ] : [],
                            newTransformedValues     = event.checked ? [ checkboxValue ] : [],
                            newValue                 = buildUpdateAndSetElementValue( removedTransformedValues, newTransformedValues );

                      if ( newValue ) {
                          // even on clear
                          if ( !useHookForm || !newTransformedValues.length ) {
                              emitOnChange( event.originalEvent );
                          }
                          else {
                              onIntermediateChange?.( newValue, event.originalEvent );

                              if ( timeoutId.current ) {
                                  clearTimeout( timeoutId.current );
                              }
                              timeoutId.current = setTimeout( emitOnChange.bind( null, event.originalEvent ), 2 * useHookFormDelay );
                          }
                      }
                  },
                  [
                      buildUpdateAndSetElementValue,
                      emitOnChange,
                      onIntermediateChange,
                      referenceDirection,
                      referenceSetId,
                      resolvedDeleteStatusId,
                      rootItemId,
                      schema,
                      useHookForm
                  ]
              ),
              onLinkButtonClick                                       = useCallback(
                  ( itemTransformedValue : TransformedValue, event : React.MouseEvent<HTMLButtonElement, MouseEvent> ) => {
                      if ( itemTransformedValue.itemId ) {
                          const targetItemTypeId = global.data[ dataContext ]?.items?.[ itemTransformedValue.itemId ]?.itemTypeId;

                          if ( targetItemTypeId ) {
                              let panelConfig : FormPanelProps | undefined = undefined;

                              if ( user ) {
                                  if ( editPanelConfigIds.length ) {
                                      panelConfig = user.getPanelConfigFromIdList(
                                          editPanelConfigIds,
                                          undefined,
                                          'Form'
                                      ) as FormPanelProps | undefined;
                                  }
                                  else if ( panelConfigFilterPrefix ) {
                                      const panelConfigs       = user.getPanelPropsForItemType( schema, targetItemTypeId, undefined, 'Form' ) as FormPanelProps[],
                                            filterPrefixRegExp = new RegExp( `^${panelConfigFilterPrefix}` );

                                      panelConfig = panelConfigs.find( panelProps => panelProps.viewKey?.match( filterPrefixRegExp ) );
                                  }
                              }

                              if ( panelConfig ) {
                                  void dispatch( openPanel(
                                      {
                                          ...panelConfig,
                                          schema,
                                          rootItemId   : itemTransformedValue.itemId,
                                          treeVariables: variables
                                      }
                                  ) );
                              }
                              else {
                                  void dispatch( openPanelForItemType(
                                      schema,
                                      targetItemTypeId,
                                      {
                                          // DON'T use dataContext since the content is deleted on form close, and it's not supported by FinderDetail
                                          schema,
                                          rootItemId: itemTransformedValue.itemId
                                      },
                                      undefined,
                                      event
                                  ) );
                              }
                          }
                      }
                  },
                  [
                      editPanelConfigIds,
                      panelConfigFilterPrefix,
                      schema,
                      user,
                      variables
                  ]
              ),
              onEditButtonClick                                       = useCallback(
                  ( itemTransformedValue : TransformedValue, event : React.MouseEvent<HTMLButtonElement, MouseEvent> ) => {
                      if ( itemTransformedValue.itemId ) {
                          const targetItemTypeId = global.data[ dataContext ]?.items?.[ itemTransformedValue.itemId ]?.itemTypeId;

                          if ( targetItemTypeId ) {
                              let panelConfig : FormPanelProps | undefined = undefined;

                              hideEditItemDialog();

                              // clone dataContext
                              for ( const item of Object.values( global.data[ dataContext ].items ) ) {
                                  item.clone( undefined, undefined, editFormDataContext );
                              }
                              for ( const reference of Object.values( global.data[ dataContext ].references ) ) {
                                  reference.clone( undefined, undefined, editFormDataContext );
                              }

                              editItem.current = global.data[ editFormDataContext ].items[ itemTransformedValue.itemId ];

                              if ( user ) {
                                  if ( editPanelConfigIds.length ) {
                                      panelConfig = user.getPanelConfigFromIdList(
                                          editPanelConfigIds,
                                          undefined,
                                          'Form'
                                      ) as FormPanelProps | undefined;
                                  }
                                  else if ( panelConfigFilterPrefix ) {
                                      const panelConfigs       = user.getPanelPropsForItemType( schema, targetItemTypeId, undefined, 'Form' ) as FormPanelProps[],
                                            filterPrefixRegExp = new RegExp( `^${panelConfigFilterPrefix}` );

                                      panelConfig = panelConfigs.find( panelProps => panelProps.viewKey?.match( filterPrefixRegExp ) );
                                  }

                                  if ( !panelConfig && referenceSets.length && targetItemTypeId ) {
                                      panelConfig = user.getPanelPropsForItemType(
                                          schema,
                                          targetItemTypeId,
                                          undefined,
                                          'Form',
                                          !!event.altKey
                                      )?.[ 0 ] as FormPanelProps | undefined;
                                  }
                              }

                              setShowEditItemDialog( true );
                              setEditPanelConfig( panelConfig ?? {} );
                          }
                      }
                  },
                  [
                      dataContext,
                      editFormDataContext,
                      editPanelConfigIds,
                      panelConfigFilterPrefix,
                      schema,
                      referenceSets,
                      user
                  ]
              ),
              onShowNewItemDialogButtonClick                          = useCallback(
                  ( event : React.MouseEvent<HTMLButtonElement, MouseEvent>, newItemTypeId? : number ) => {
                      const itemTypeId = newItemTypeId ?? targetItemTypeIds[ 0 ];

                      if ( itemTypeId ) {
                          const asyncFnc = async () => {
                              let panelConfig : FormPanelProps | undefined = undefined;

                              hideEditItemDialog();

                              const newItem = new Item( { itemTypeId }, editFormDataContext, schema );

                              await props.onCreateNewItemForReference?.( newItem, event );

                              // clone dataContext
                              for ( const item of Object.values( global.data[ dataContext ].items ) ) {
                                  item.clone( undefined, undefined, editFormDataContext );
                              }
                              for ( const reference of Object.values( global.data[ dataContext ].references ) ) {
                                  reference.clone( undefined, undefined, editFormDataContext );
                              }
                              global.data[ editFormDataContext ].items[ newItem.id ] = newItem;

                              editItem.current = newItem;

                              ( document.activeElement as HTMLElement | undefined )?.blur();

                              if ( user ) {
                                  if ( editPanelConfigIds.length ) {
                                      panelConfig = user.getPanelConfigFromIdList(
                                          editPanelConfigIds,
                                          undefined,
                                          'Form'
                                      ) as FormPanelProps | undefined;
                                  }
                                  else if ( panelConfigFilterPrefix ) {
                                      const panelConfigs       = user.getPanelPropsForItemType( schema, itemTypeId, undefined, 'Form' ) as FormPanelProps[],
                                            filterPrefixRegExp = new RegExp( `^${panelConfigFilterPrefix}` );

                                      panelConfig = panelConfigs.find( panelProps => panelProps.viewKey?.match( filterPrefixRegExp ) );
                                  }

                                  if ( !panelConfig && referenceSets.length ) {
                                      panelConfig = user.getPanelPropsForItemType(
                                          schema,
                                          itemTypeId,
                                          undefined,
                                          'Form',
                                          !!event?.altKey
                                      )?.[ 0 ] as FormPanelProps | undefined;
                                  }
                              }

                              setShowEditItemDialog( true );
                              setEditPanelConfig( panelConfig ?? {} );
                          };

                          void asyncFnc();
                      }
                  },
                  [
                      editFormDataContext,
                      editPanelConfigIds,
                      panelConfigFilterPrefix,
                      referenceSets,
                      schema,
                      targetItemTypeIds,
                      user
                  ]
              ),
              newItemTypeMenuItems                                    = useMemo(
                  () => {
                      return targetItemTypeIds
                          .map( itemTypeId => ( {
                              icon   : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( itemTypeId )?.icon}`,
                              label  : getItemTypeDisplayName( itemTypeId, schema ),
                              command: ( event : MenuItemCommandEvent ) => onShowNewItemDialogButtonClick(
                                  event.originalEvent as unknown as React.MouseEvent<HTMLButtonElement, MouseEvent>,
                                  itemTypeId
                              )
                          } ) )
                          .sort( ( a, b ) => a.label.localeCompare( b.label ) );
                  },
                  [
                      onShowNewItemDialogButtonClick,
                      schema,
                      targetItemTypeIds
                  ]
              ),
              hideEditItemDialog                                      = useCallback(
                  () => {
                      if ( editItem.current ) {
                          editItem.current = null;
                      }
                      if ( editFormDataContext !== dataContext ) {
                          delete global.data[ editFormDataContext ];
                      }

                      setShowEditItemDialog( false );

                      setTimeout(
                          () => {
                              dispatch( clearPanelProps( editFormUid ) );
                              dispatch( clearElusiveData( editFormUid ) );
                              dispatch( clearElusiveDataForSubpanels( editFormUid ) );
                          },
                          1
                      );
                  },
                  [
                      dataContext,
                      editFormDataContext,
                      editFormUid
                  ]
              ),
              onEditItemDialogHide                                    = useCallback(
                  () => {
                      hideEditItemDialog();
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onEditItemDialogOkButtonClick                           = useCallback(
                  ( event : React.MouseEvent<HTMLButtonElement, MouseEvent> ) => {
                      setShowEditItemDialog( false );

                      if ( editItem.current ) {
                          const item : Item                                             = global.data[ editFormDataContext ].items[ editItem.current.id ],
                                existingTransformedValue : TransformedValue | undefined = currentElementValue.current.find( cEV => cEV.itemId === item.id ),
                                removedTransformedValues : TransformedValue[]           = [],
                                newTransformedValues : TransformedValue[]               = [],
                                updatedTransformedValues : TransformedValue[]           = [];

                          let additionalData : ItemsAndReferencesCollection | undefined = {
                              items     : Object.values( global.data[ editFormDataContext ].items ?? {} ),
                              references: Object.values( global.data[ editFormDataContext ].references ?? {} )
                          };

                          // remove editItem from additionalData
                          additionalData.items.splice( additionalData.items.findIndex( i => i === item ), 1 );

                          // delete additionalData if empty
                          if ( !additionalData.items.length && !additionalData.references.length ) {
                              additionalData = undefined;
                          }

                          if ( !existingTransformedValue ) {
                              if ( !multi && currentElementValue.current[ 0 ] ) {
                                  removedTransformedValues.push( currentElementValue.current[ 0 ] );

                                  // remove oldItem and oldReference
                                  if ( additionalData ) {
                                      let index = additionalData.items.findIndex( i => i.id === currentElementValue.current[ 0 ].itemId );
                                      if ( index !== -1 ) {
                                          additionalData.items.splice( index, 1 );
                                      }

                                      index = additionalData.references.findIndex( r => r.id === currentElementValue.current[ 0 ].id );
                                      if ( index !== -1 ) {
                                          additionalData.references?.splice( index, 1 );
                                      }
                                  }
                              }

                              newTransformedValues.push( {
                                  key      : `item_0`,
                                  id       : 0,
                                  statusId : resolvedNewStatusId,
                                  label    : '',
                                  sortLabel: '',
                                  itemId   : item.id,
                                  item,
                                  additionalData
                              } );
                          }
                          else {
                              updatedTransformedValues.push( { ...existingTransformedValue } );
                              updatedTransformedValues[ 0 ].item = item;
                              updatedTransformedValues[ 0 ].additionalData = additionalData
                          }

                          const newValue = buildUpdateAndSetElementValue( removedTransformedValues, newTransformedValues, updatedTransformedValues );

                          if ( newValue ) {
                              if ( !useHookForm ) {
                                  emitOnChange( event );
                              }
                              else {
                                  onIntermediateChange?.( newValue, event );

                                  if ( timeoutId.current ) {
                                      clearTimeout( timeoutId.current );
                                  }
                                  timeoutId.current = setTimeout( emitOnChange.bind( null, event ), 2 * useHookFormDelay );
                              }
                          }
                      }

                      hideEditItemDialog();
                  },
                  [
                      buildUpdateAndSetElementValue,
                      dataContext,
                      emitOnChange,
                      multi,
                      onIntermediateChange,
                      referenceDirection,
                      referenceSetId,
                      resolvedDeleteStatusId,
                      resolvedNewStatusId,
                      rootItemId,
                      schema,
                      useHookForm
                  ]
              ),
              onEditItemDialogCancelButtonClick                       = useCallback(
                  () => {
                      hideEditItemDialog();
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              valueTemplate                                           = useCallback(
                  ( itemTransformedValue : TransformedValue ) : React.ReactNode => {
                      return itemTransformedValue?.label ?? resolvedPaceholder ?? '';
                  },
                  [
                      resolvedPaceholder
                  ]
              ),
              itemTemplate                                            = useCallback(
                  ( itemTransformedValue : TransformedValue ) : React.ReactNode => {
                      // faked group header row of a polymorphic ('*') binding — not selectable
                      if ( itemTransformedValue?.isGroupHeader ) {
                          return <div className="yn-referencefield-suggestion-header">{itemTransformedValue.groupLabel}</div>;
                      }

                      return itemTransformedValue?.searchLabel ?? itemTransformedValue?.label ?? '';
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              multiSelectSelectedItemTemplate                         = useCallback(
                  ( itemTransformedValue : TransformedValue ) : React.ReactNode => {
                      let retVal : React.ReactNode | undefined;

                      if ( itemTransformedValue?.label ) {
                          retVal = <div className="p-multiselect-token">
                              <span className="p-multiselect-token-label">{itemTransformedValue.label}</span>
                              {linkable && itemTransformedValue?.itemId > 0 &&
                                  <>
                                      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                                      <span
                                          className="p-multiselect-token-icon pi pi-external-link yn-enabled"
                                          /* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events */
                                          onMouseOver={( elem ) => {
                                              ( elem.target as HTMLElement ).classList.add( 'yn-blue' );
                                          }}
                                          onMouseLeave={( elem ) => {
                                              ( elem.target as HTMLElement ).classList.remove( 'yn-blue' );
                                          }}
                                          onClick={linkTarget === 'panel' ? onLinkButtonClick.bind( null, itemTransformedValue ) : onEditButtonClick.bind( null, itemTransformedValue )}
                                      />
                                  </>
                              }
                              {newAddable && itemTransformedValue?.itemId < 0 &&
                                  <>
                                      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                                      <span
                                          className="p-multiselect-token-icon pi pi-pencil"
                                          /* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events */
                                          onMouseOver={( elem ) => {
                                              ( elem.target as HTMLElement ).classList.add( 'yn-blue' );
                                          }}
                                          onMouseLeave={( elem ) => {
                                              ( elem.target as HTMLElement ).classList.remove( 'yn-blue' );
                                          }}
                                          onClick={onEditButtonClick.bind( null, itemTransformedValue )}
                                      />
                                  </>
                              }
                              {deleteButton &&
                                  <>
                                      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                                      <span
                                          className="p-multiselect-token-icon pi pi-times-circle"
                                          /* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events */
                                          onMouseOver={( elem ) => {
                                              ( elem.target as HTMLElement ).classList.add( 'yn-blue' );
                                          }}
                                          onMouseLeave={( elem ) => {
                                              ( elem.target as HTMLElement ).classList.remove( 'yn-blue' );
                                          }}
                                          onClick={onMultiSelectRemove.bind( null, itemTransformedValue )}
                                      />
                                  </>
                              }
                          </div>;
                      }
                      else {
                          retVal = resolvedPaceholder ?? ' ';
                      }

                      return retVal;
                  },
                  [
                      deleteButton,
                      linkable,
                      newAddable,
                      onEditButtonClick,
                      onLinkButtonClick,
                      onMultiSelectRemove,
                      resolvedPaceholder
                  ]
              ),
              onFilter                                                = useCallback(
                  ( event : DropdownFilterEvent ) => {
                      if ( filter.current !== event.filter && ( event.filter.length >= autoCompleteMinLength || event.filter === '' ) ) {
                          filter.current = event.filter;

                          if ( filterTimeoutId.current ) {
                              clearTimeout( filterTimeoutId.current );
                          }

                          filterTimeoutId.current = setTimeout(
                              () => {
                                  setSearchItems( Array.from( { length: 101 } ) as TransformedValue[] );
                                  first.current = 0;
                                  last.current = 101;
                                  setLoad( true );
                              },
                              autoCompleteDelay
                          );
                      }
                  },
                  [
                      autoCompleteDelay,
                      autoCompleteMinLength
                  ]
              ),
              onHide                                                  = useCallback(
                  () => {
                      setLoad( false );
                      setOverlay( false );
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onLazyLoad                                              = useCallback(
                  ( event : VirtualScrollerLazyEvent ) => {
                      const eFirst = event.first as number,
                            eLast  = ( event.last as number ) - 1;

                      if ( first.current !== eFirst || last.current !== eLast ) {
                          first.current = eFirst;
                          last.current = eLast;
                          setLoad( true );
                      }
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              valueToUseForCaching                                    = useMemo(
                  () => {
                      return transformedValue !== currentTransformedValue.current ? transformedValue : elementValue;
                  },
                  [
                      transformedValue,
                      elementValue
                  ]
              ),
              newItemButtons                                          = useCallback(
                  ( buttonTabIndex : number ) : React.ReactNode[] => {
                      const polymorphic = targetItemTypeIds.length > 1;

                      return [
                          <Button
                              key={`${uid}_showNewItemDialogButton`}

                              icon="fas fa-plus"
                              className="yn-add"
                              onClick={polymorphic
                                  ? ( event : React.MouseEvent<HTMLButtonElement, MouseEvent> ) => newItemTypeMenuRef.current?.toggle( event )
                                  : onShowNewItemDialogButtonClick}

                              onFocus={onButtonFocus}
                              onBlur={onButtonBlur}

                              disabled={disabled}

                              tabIndex={buttonTabIndex}

                              aria-controls={polymorphic ? `${uid}_newItemTypeMenu` : `${uid}_editItemDialog`}
                              aria-haspopup
                          />,
                          ...( polymorphic
                              ? [
                                  <TieredMenu
                                      key={`${uid}_newItemTypeMenu`}
                                      id={`${uid}_newItemTypeMenu`}
                                      ref={newItemTypeMenuRef}

                                      model={newItemTypeMenuItems}
                                      popup
                                      appendTo={appendTo === 'self' ? document.body : appendTo}
                                  />
                              ]
                              : [] )
                      ];
                  },
                  [
                      appendTo,
                      disabled,
                      newItemTypeMenuItems,
                      onButtonBlur,
                      onButtonFocus,
                      onShowNewItemDialogButtonClick,
                      targetItemTypeIds,
                      uid
                  ]
              ),
              fieldContent : JSX.Element | JSX.Element[ ] | undefined = useMemo(
                  () => {
                      const buttons : React.ReactNode[] = [];

                      let retValElements : JSX.Element | JSX.Element[] | undefined;

                      // error if only single reference is allowed but more given
                      if ( !multi && valueToUseForCaching.length > 1 ) {
                          retValElements = <StaticContentField
                              {..._.pick( props, staticContentFieldComponentProps ) as StaticContentFieldComponentProps}

                              label={undefined}
                              labelVisibility="hidden"
                              infoTextLayout="text"
                              height={1}
                              content="YN.base.form.referenceField.error.singleRefTargetButMultipleValues"
                              inputClassNames={[ 'yn-dummy-field', 'yn-error' ]}
                              messageClassNames={[ 'hidden' ]}
                              inputStyle={getHeightStyle( {}, 1, 0, labelVisibility, labelPosition, infoTextLayout )}
                              style={{ marginBottom: 0 }}
                          />;
                      }

                      // error if display checkboxes and new items addable
                      else if ( presentation === 'checkboxes' && newAddable ) {
                          retValElements = <StaticContentField
                              {..._.pick( props, staticContentFieldComponentProps ) as StaticContentFieldComponentProps}

                              label={undefined}
                              labelVisibility="hidden"
                              infoTextLayout="text"
                              height={1}
                              content="YN.base.form.referenceField.error.checkboxesAndNewAddable"
                              inputClassNames={[ 'yn-dummy-field', 'yn-error' ]}
                              messageClassNames={[ 'hidden' ]}
                              inputStyle={getHeightStyle( {}, 1, 0, labelVisibility, labelPosition, infoTextLayout )}
                              style={{ marginBottom: 0 }}
                          />;
                      }

                      // error if display checkboxes but no existing items addable
                      else if ( presentation === 'checkboxes' && !existingAddable ) {
                          retValElements = <StaticContentField
                              {..._.pick( props, staticContentFieldComponentProps ) as StaticContentFieldComponentProps}

                              label={undefined}
                              labelVisibility="hidden"
                              infoTextLayout="text"
                              height={1}
                              content="YN.base.form.referenceField.error.checkboxesButNoExistingAddable"
                              inputClassNames={[ 'yn-dummy-field', 'yn-error' ]}
                              messageClassNames={[ 'hidden' ]}
                              inputStyle={getHeightStyle( {}, 1, 0, labelVisibility, labelPosition, infoTextLayout )}
                              style={{ marginBottom: 0 }}
                          />;
                      }

                      // single referenceSet target as dropdown
                      else if ( !multi && presentation === 'dropdown' ) {
                          const panelClassNames : string[]        = [],
                                fieldOptions : TransformedValue[] = [ ...searchItems ],
                                transformedValueIndex             = valueToUseForCaching[ 0 ] ? fieldOptions.findIndex( sI => sI?.itemId === valueToUseForCaching[ 0 ].itemId ) : -1;

                          if ( transformedValueIndex === -1 ) {
                              fieldOptions.unshift( valueToUseForCaching[ 0 ] );
                          }
                          else {
                              fieldOptions.splice( transformedValueIndex, 1, valueToUseForCaching[ 0 ] );
                          }

                          // polymorphic ('*') binding: the candidate list is already clustered by
                          // itemType with injected group headers — a re-sort would scatter the headers
                          if ( referenceSets.length <= 1 ) {
                              sortTransformedValue( fieldOptions );
                          }

                          if ( ( linkable && valueToUseForCaching[ 0 ]?.itemId > 0 ) || ( newAddable && valueToUseForCaching[ 0 ]?.itemId < 0 ) ) {
                              panelClassNames.push( 'yn-referencefield-linkable' );
                          }
                          if ( newAddable ) {
                              panelClassNames.push( 'yn-referencefield-addable' );
                          }

                          retValElements = <Dropdown
                              id={`${uid}_referenceDropdown`}
                              key={`${uid}_referenceDropdown`}
                              ref={dropdownComponentRef}

                              value={valueToUseForCaching[ 0 ]}

                              options={[ ...fieldOptions ]}
                              dataKey="itemId"
                              optionLabel="sortLabel"

                              onChange={!disabled && !readOnly ? onDropdownChange : undefined}
                              valueTemplate={valueTemplate}
                              itemTemplate={itemTemplate}

                              disabled={disabled || readOnly}

                              placeholder={resolvedPaceholder}

                              appendTo={appendTo}
                              panelClassName={panelClassNames.join( ' ' )}

                              className={`yn-referencefield yn-referencefield-single ${inputClassNames.join( ' ' )} ${existingAddable ? '' : 'yn-referencefield-noselection'} ${focus || overlay ? ' p-focus' : ''}`}
                              style={{ ...defaultProps.inputStyle, ...inputStyle }}

                              filter
                              showClear={deleteButton}
                              onFilter={onFilter}

                              onFocus={onInputFocus}
                              onBlur={onInputBlur}

                              showOnFocus={existingAddable}

                              onShow={onDropdownShow}
                              onKeyDown={!existingAddable ? onDropdownKeyDown : undefined}
                              onHide={onHide}

                              tabIndex={tabIndex}

                              virtualScrollerOptions={{
                                  lazy             : true,
                                  onLazyLoad       : onLazyLoad,
                                  itemSize         : 27,
                                  delay            : 0,
                                  showLoader       : true,
                                  loading          : searchItemsLoading,
                                  loadingTemplate  : ( options ) => {
                                      return <div className="flex align-items-center p-2" style={{ height: '27px' }}>
                                          <Skeleton width={`${30 + Math.round( Math.random() * 60 )}%`} height="1rem"/>
                                      </div>;
                                  },
                                  numToleratedItems: 50
                              }}
                          />;

                          if ( linkable && valueToUseForCaching[ 0 ]?.itemId > 0 ) {
                              buttons.push( <Button
                                  key={`${uid}_linkButton`}

                                  icon="pi pi-external-link"
                                  className="yn-enabled"
                                  onClick={linkTarget === 'panel' ? onLinkButtonClick.bind( null, valueToUseForCaching[ 0 ] ) : onEditButtonClick.bind( null, valueToUseForCaching[ 0 ] )}

                                  onFocus={onButtonFocus}
                                  onBlur={onButtonBlur}

                                  tabIndex={tabIndex ? tabIndex + 20 : 0}
                              /> );
                          }

                          if ( newAddable ) {
                              // edit button for new item
                              if ( valueToUseForCaching[ 0 ]?.itemId < 0 ) {
                                  buttons.push( <Button
                                      key={`${uid}_linkButton`}

                                      icon="pi pi-pencil"
                                      onClick={onEditButtonClick.bind( null, valueToUseForCaching[ 0 ] )}

                                      onFocus={onButtonFocus}
                                      onBlur={onButtonBlur}

                                      disabled={disabled}

                                      tabIndex={tabIndex ? tabIndex + 21 : 0}
                                  /> );
                              }

                              buttons.push( ...newItemButtons( tabIndex ? tabIndex + 22 : 0 ) );
                          }
                      }

                      // single referenceSet targets as radios
                      else if ( !multi ) {
                          // TODO: implement if needed
                      }

                      // multiple referenceSet targets as multiselection dropdown
                      else if ( multi && presentation === 'dropdown' ) {
                          const panelClassNames : string[]        = [],
                                fieldOptions : TransformedValue[] = [ ...searchItems ];

                          for ( let i = 0; i < valueToUseForCaching.length; i++ ) {
                              const transformedValueIndex = valueToUseForCaching[ i ] ? fieldOptions.findIndex( sI => sI?.itemId === valueToUseForCaching[ i ].itemId ) : -1;

                              if ( transformedValueIndex === -1 ) {
                                  fieldOptions.unshift( valueToUseForCaching[ i ] );
                              }
                              else {
                                  fieldOptions.splice( transformedValueIndex, 1, valueToUseForCaching[ i ] );
                              }
                          }

                          // polymorphic ('*') binding: keep the getData clustering + group headers
                          if ( referenceSets.length <= 1 ) {
                              sortTransformedValue( fieldOptions );
                          }

                          if ( newAddable ) {
                              panelClassNames.push( 'yn-referencefield-addable' );
                          }

                          retValElements = <MultiSelect
                              id={`${uid}_referenceMultiSelect`}
                              key={`${uid}_referenceMultiSelect`}
                              ref={multiSelectComponentRef}

                              value={valueToUseForCaching}

                              options={fieldOptions}
                              dataKey="itemId"
                              optionLabel="sortLabel"

                              onChange={!disabled && !readOnly ? onMultiSelectChange : undefined}
                              itemTemplate={itemTemplate}
                              selectedItemTemplate={multiSelectSelectedItemTemplate}

                              display="chip"

                              disabled={disabled || readOnly}

                              placeholder={resolvedPaceholder}

                              appendTo={appendTo}
                              panelClassName={panelClassNames.join( ' ' )}

                              className={`yn-referencefield yn-referencefield-multi ${inputClassNames.join( ' ' )} ${existingAddable ? '' : 'yn-referencefield-noselection'} ${focus || overlay ? 'p-focus' : ''}`}
                              style={{ ...defaultProps.inputStyle, ...inputStyle }}

                              filter
                              showClear={deleteButton}
                              onFilter={onFilter}

                              onFocus={onInputFocus}
                              onBlur={onInputBlur}
                              onShow={onMultiSelectShow}
                              onKeyDown={!existingAddable ? onMultiselectKeyDown : undefined}
                              onHide={onHide}

                              tabIndex={tabIndex}

                              virtualScrollerOptions={{
                                  lazy             : true,
                                  onLazyLoad       : onLazyLoad,
                                  itemSize         : 27,
                                  delay            : 0,
                                  showLoader       : true,
                                  loading          : searchItemsLoading,
                                  loadingTemplate  : ( options ) => {
                                      return <div className="flex align-items-center p-2" style={{ height: '27px' }}>
                                          <Skeleton width={`${30 + Math.round( Math.random() * 60 )}%`} height="1rem"/>
                                      </div>;
                                  },
                                  numToleratedItems: 50
                              }}
                          />;

                          if ( newAddable ) {
                              buttons.push( ...newItemButtons( tabIndex ? tabIndex + 20 : 0 ) );
                          }
                      }

                      // multiple referenceSet targets as checkboxes
                      else {
                          const fieldOptions : TransformedValue[] = [ ...searchItems ];

                          /* add all options set even they are not loaded by query
                          for ( let i = 0; i < valueToUseForCaching.length; i++ ) {
                              const transformedValueIndex = valueToUseForCaching[ i ] ? fieldOptions.findIndex( sI => sI?.itemId === valueToUseForCaching[ i ].itemId ) : -1;

                              if ( transformedValueIndex === -1 ) {
                                  fieldOptions.unshift( valueToUseForCaching[ i ] );
                              }
                              else {
                                  fieldOptions.splice( transformedValueIndex, 1, valueToUseForCaching[ i ] );
                              }
                          }

                          sortTransformedValue( fieldOptions );*/

                          retValElements = <div
                              className="yn-referencefield-checkbox"
                              style={{ columnCount: presentationColumns || 1 }}
                          >
                              {fieldOptions.map( ( option, optionIndex ) => {
                                  if ( option ) {
                                      const transformedValueIndex       = valueToUseForCaching.findIndex( sI => sI?.itemId === option.itemId ),
                                            originalValueIndex          = ( Array.isArray( originalValue ) ? originalValue : [ originalValue ] )?.findIndex( oV => oV?.[ referenceDirection === 'SrcToDest' ? 'destItemId' : 'srcItemId' ] === option.itemId ),
                                            optionTransformedValue      = valueToUseForCaching[ transformedValueIndex ],
                                            optionElementId             = `${elementId}_${optionIndex}`,
                                            optionClassNames : string[] = [],
                                            checked                     = transformedValueIndex !== -1;

                                      if (
                                          markDirty
                                          && (
                                              ( checked && optionTransformedValue?.id < 1 )
                                              || ( !checked && originalValueIndex !== -1 )
                                          )
                                      ) {
                                          optionClassNames.push( 'yn-dirty-border' );
                                      }

                                      return <div key={optionElementId} className={`field-checkbox ${optionClassNames.join( ' ' )}`}>
                                          <Checkbox
                                              inputId={optionElementId}

                                              value={option}
                                              onChange={!disabled && !readOnly ? onCheckboxChange.bind( null, optionTransformedValue ?? option ) : undefined}

                                              checked={checked}
                                              disabled={disabled || readOnly}

                                              tooltip={undefined}
                                              tooltipOptions={undefined}

                                              tabIndex={( tabIndex ? tabIndex + 30 : 0 ) + 3 * optionIndex}
                                          />
                                          <label
                                              htmlFor={optionElementId}
                                              className={`p-checkbox-label`}
                                              style={{ ...defaultProps.inputStyle, ...inputStyle }}
                                          >
                                              {option.label}
                                          </label>
                                          {linkable && optionTransformedValue?.itemId > 0 &&
                                              <Button
                                                  key={`${uid}_linkButton`}

                                                  icon="pi pi-external-link"
                                                  className="yn-link-button yn-enabled"
                                                  onClick={linkTarget === 'panel' ? onLinkButtonClick.bind( null, optionTransformedValue ) : onEditButtonClick.bind( null, optionTransformedValue )}
                                                  link={true}
                                                  onFocus={onButtonFocus}
                                                  onBlur={onButtonBlur}

                                                  tabIndex={( tabIndex ? tabIndex + 30 : 0 ) + 3 * optionIndex + 1}
                                              />
                                          }
                                      </div>;
                                  }
                              } )}
                          </div>;
                      }

                      if (
                          (
                              newAddable
                              || linkTarget === 'popup'
                          )
                          && editItem.current
                          && showEditItemDialog
                      ) {
                          retValElements = <>
                              {retValElements}
                              <Dialog
                                  key={`${uid}_editItemDialog`}
                                  id={`${uid}_editItemDialog`}

                                  visible={true}
                                  onHide={onEditItemDialogHide}
                                  focusOnShow={false}
                                  closeOnEscape={false}
                                  className="yn-form-dialog"
                                  header={popupTitle ? __( popupTitle ) : __( 'YN.base.form.referenceField.item.create', { itemTypeName: editItem.current.itemType.label } )}
                                  footer={<div>
                                      <Button
                                          id={`${editFormUid}_okButton`}
                                          key="okButton"
                                          label={__( 'YN.base.common.ok' )}
                                          className="p-button-outlined mr-3"
                                          disabled={editFormElusiveData?.errorData?.count ?? 1}
                                          onClick={onEditItemDialogOkButtonClick}
                                      />
                                      <Button
                                          id={`${editFormUid}_cancelButton`}
                                          key="cancelButton"
                                          label={__( 'YN.base.common.cancel' )}
                                          className="p-button-secondary p-button-outlined"
                                          onClick={onEditItemDialogCancelButtonClick}
                                      />
                                  </div>}
                              >
                                  <LoadingMaskPanel
                                      key={`${editFormUid}_loadingMask`}
                                      forComponent={editFormUid}
                                      className="yn-flex-layout-parent"
                                  >
                                      <Toast
                                          ref={toast}
                                          content=""

                                          key={`${editFormUid}_toast`}

                                          position="bottom-right"
                                          className="yn-toast-absolute"
                                      />
                                      {( editPanelConfig.panelType === 'Form' ) &&
                                          <Form
                                              {...additionalAddItemFormProps}

                                              key={editFormUid}
                                              uid={editFormUid}
                                              dataContext={editFormDataContext}
                                              additionalCheckDataContexts={[ dataContext ]}

                                              schema={schema}
                                              viewKey={editPanelConfig.viewKey}
                                              rootItemId={editItem.current.id}
                                              mode="popup"

                                              tabIndexOffset={tabIndexOffset}

                                              onDataLoad={() => {
                                                  setForceRender( forceRender + 1 );
                                              }}

                                              treeVariables={variables}

                                              {...editFormProps}
                                          />
                                      }
                                      {( editPanelConfig.panelType !== 'Form' ) &&
                                          <GenericForm
                                              key={editFormUid}
                                              uid={editFormUid}
                                              dataContext={editFormDataContext}
                                              additionalCheckDataContexts={[ dataContext ]}

                                              schema={schema}
                                              item={editItem.current}
                                              editable

                                              onErrorDataChanged={() => {
                                                  setForceRender( forceRender + 1 );
                                              }}

                                              {...editFormProps}
                                          />
                                      }
                                  </LoadingMaskPanel>
                              </Dialog>
                          </>;
                      }

                      return <div
                          id={elementId}
                          className="yn-referencefield-wrapper"
                          style={{ ...defaultProps.inputStyle, ...inputStyle, ...heightStyle }}
                      >
                          <div
                              id={`${elementId}_wrapper`}
                              className={`${presentation !== 'checkboxes' ? 'p-inputgroup yn-inputgroup' : ''} yn-subfield ${focus || overlay ? 'p-focus' : ''} ${disabled || readOnly ? 'p-disabled' : ''}`}
                          >
                              {retValElements}
                              {buttons}
                          </div>
                      </div>;
                  },
                  [
                      appendTo,
                      deleteButton,
                      disabled,
                      editFormDataContext,
                      editFormUid,
                      editFormElusiveData?.data?.items?.[ editItem.current?.id ?? 0 ] ?? 0,
                      editFormElusiveData?.errorData?.count ?? 1,
                      JSON.stringify( editFormProps ),
                      existingAddable,
                      focus,
                      forceRender,
                      JSON.stringify( heightStyle ),
                      JSON.stringify( inputClassNames ),
                      JSON.stringify( inputStyle ),
                      load,
                      messageResolver,
                      multi,
                      newAddable,
                      newItemButtons,
                      onEditButtonClick,
                      onLinkButtonClick,
                      JSON.stringify( originalValue ), // check if originalValue really changed
                      overlay,
                      popupTitle,
                      presentation,
                      presentationColumns,
                      readOnly,
                      referenceDirection,
                      referenceSets,
                      resolvedPaceholder,
                      searchItems,
                      searchItemsLoading,
                      showEditItemDialog,
                      tabIndexOffset,
                      user ? user.language : undefined,
                      valueToUseForCaching,
                      variables
                  ]
              );

        // use refs for dynamic resolving
        currentValue.current = value;
        currentElementValue.current = elementValue;
        currentTransformedValue.current = transformedValue;
        currentValueVersions.current = valueVersions;

        useEffect(
            () => {
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                if ( dropdownComponentRef.current || multiSelectComponentRef.current ) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    document.addEventListener( 'keydown', onDocumentKeyDown, true );
                }
            },
            []
        );

        // on unmount
        useEffect(
            () => {
                return () => {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    document.removeEventListener( 'keydown', onDocumentKeyDown, true );
                };
            },
            []
        );

        useEffect(
            () => {
                setElementValue( transformedValue );
                outValue.current = undefined;
            },
            [
                transformedValue
            ]
        );

        // resolve appendTo
        useLayoutEffect(
            () => {
                if ( appendToElementId && appendToElementId !== 'self' ) {
                    let appendToElement : HTMLElement | null;

                    if ( appendToElementId !== 'document' ) {
                        appendToElement = document.getElementById( appendToElementId );
                    }
                    else {
                        appendToElement = document.body;
                    }

                    if ( appendToElement && appendToElement !== appendTo ) {
                        setAppendTo( appendToElement );
                    }
                }
            }
        );

        // show editItem form toast
        useLayoutEffect(
            () => {
                if ( toast.current && editItemFormIntercomMessages?.toast ) {
                    const toastKeys = Object.keys( editItemFormIntercomMessages.toast ) as string[];

                    for ( const key of toastKeys ) {
                        // show toast
                        if ( editItemFormIntercomMessages.toast[ key ] ) {
                            toast.current.show( editItemFormIntercomMessages.toast[ key ] );
                        }

                        // clear toast message
                        dispatch( unsetMessage( uid, 'toast', key ) );
                    }
                }
            },
            [
                editItemFormIntercomMessages
            ]
        );

        // mark chips invalid or dirty if
        useEffect(
            () => {
                if ( multi && multiSelectComponentRef.current ) {
                    const chipsElement = multiSelectComponentRef.current.getElement();

                    for ( const tV of elementValue ) {
                        const chipElement = chipsElement.querySelector( `#item_${elementId}_${tV.key}` )?.parentElement?.parentElement;

                        if ( chipElement ) {
                            if ( tV.id < 1 ) {
                                chipElement.classList.add( 'yn-chip-dirty' );
                            }
                            else {
                                chipElement.classList.remove( 'yn-chip-dirty' );
                            }
                        }
                    }
                }
            },
            [
                multi,
                elementValue
            ]
        );

        // add click handlers
        useEffect(
            () => {
                if ( !existingAddable ) {
                    if ( !multi ) {
                        dropdownComponentRef.current?.getElement()?.addEventListener( 'click', onDropdownClick );
                    }
                    else {
                        multiSelectComponentRef.current?.getElement()?.addEventListener( 'click', onMultiselectClick );
                    }
                }
            }
        );

        // realign overlay and set tabIndex to filter if
        useLayoutEffect(
            () => {
                let overlayTarget : HTMLElement | undefined,
                    target : HTMLElement | undefined,
                    overlayFilterInput : HTMLInputElement | undefined,
                    targetClearTrigger : HTMLElement | undefined,
                    targetDownTrigger : HTMLElement | undefined;

                if ( !multi && dropdownComponentRef.current ) {
                    overlayTarget = dropdownComponentRef.current.getOverlay();
                    target = dropdownComponentRef.current.getElement();

                    overlayFilterInput = overlayTarget?.querySelector( 'input.p-dropdown-filter' ) as HTMLInputElement | undefined;
                    targetClearTrigger = ( target?.querySelector( 'i.p-dropdown-clear-icon' ) ?? target?.querySelector( 'svg.p-dropdown-clear-icon' ) ) as HTMLElement | undefined;
                    targetDownTrigger = target?.querySelector( 'div.p-dropdown-trigger' ) as HTMLElement | undefined;
                }
                else if ( multi && multiSelectComponentRef.current ) {
                    overlayTarget = multiSelectComponentRef.current.getOverlay();
                    target = multiSelectComponentRef.current.getElement();

                    overlayFilterInput = overlayTarget?.querySelector( 'input.p-multiselect-filter' ) as HTMLInputElement | undefined;
                    targetClearTrigger = ( target?.querySelector( 'i.p-multiselect-clear-icon' ) ?? target?.querySelector( 'svg.p-dropdown-clear-icon' ) ) as HTMLElement | undefined;
                    targetDownTrigger = target?.querySelector( 'div.p-multiselect-trigger' ) as HTMLElement | undefined;
                }

                if ( overlayTarget && target ) {
                    // wrong TypeScript definition for DomHandler.alignOverlay
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    DomHandler.alignOverlay( overlayTarget, target, appendTo ?? document.body );

                    if ( overlayTarget.style.transformOrigin.indexOf( 'bottom' ) !== -1 ) {
                        overlayTarget.style.marginTop = '-2px';
                    }
                    else {
                        overlayTarget.style.marginTop = '2px';
                    }

                    if ( overlayTarget.classList.contains( 'yn-referencefield-linkable' ) && overlayTarget.classList.contains( 'yn-referencefield-addable' ) ) {
                        overlayTarget.style.width = `calc(${target.getBoundingClientRect().width}px + 3.714rem)`; // 2x .yn-inputgroup .p-button.p-button-icon-only width
                    }
                    else if ( overlayTarget.classList.contains( 'yn-referencefield-linkable' ) || overlayTarget.classList.contains( 'yn-referencefield-addable' ) ) {
                        overlayTarget.style.width = `calc(${target.getBoundingClientRect().width}px + 1.857rem)`; // .yn-inputgroup .p-button.p-button-icon-only width
                    }

                    // Beim Anhängen ans document richtet PrimeReact ( DomHandler.absolutePosition ) das Overlay am
                    // rechten Rand aus, sobald `feldLinks + feldBreite + overlayBreite` breiter als das Fenster ist –
                    // die Feldbreite wird dort doppelt gerechnet, in Randspalten ( z. B. Properties-Panel ) sitzt das
                    // Overlay dadurch immer versetzt. Deshalb hier auf die linke Feldkante zurücksetzen, solange das
                    // Overlay ins Fenster passt.
                    if ( appendTo !== 'self' ) {
                        const targetLeft   = target.getBoundingClientRect().left + window.scrollX,
                              maxLeft      = document.documentElement.clientWidth - overlayTarget.offsetWidth + window.scrollX;

                        overlayTarget.style.left = `${Math.max( 0, Math.min( targetLeft, maxLeft ) )}px`;
                    }
                }

                if ( tabIndex ) {
                    overlayFilterInput?.setAttribute( 'tabindex', `${tabIndex + 1}` );

                    targetClearTrigger?.setAttribute( 'tabindex', `${tabIndex + 2}` );
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    targetClearTrigger?.removeEventListener( 'focus', onTriggerFocus );
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    targetClearTrigger?.addEventListener( 'focus', onTriggerFocus );
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    targetClearTrigger?.removeEventListener( 'blur', onTriggerBlur );
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    targetClearTrigger?.addEventListener( 'blur', onTriggerBlur );

                    targetDownTrigger?.setAttribute( 'tabindex', `${tabIndex + 3}` );
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    targetDownTrigger?.removeEventListener( 'focus', onTriggerFocus );
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    targetDownTrigger?.addEventListener( 'focus', onTriggerFocus );
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    targetDownTrigger?.removeEventListener( 'blur', onTriggerBlur );
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    targetDownTrigger?.addEventListener( 'blur', onTriggerBlur );
                }
            }
        );

        // load data if
        useEffect(
            () => {
                if (
                    user
                    && (
                        load
                        || (
                            presentation === 'checkboxes'
                        )
                    )
                ) {
                    const limit                    = last.current > 0 ? last.current - first.current : 100,
                          itemTypeNames : string[] = [ ...new Set( referenceSets.map( rSet => referenceDirection === 'SrcToDest' ? rSet.destItemType.name : rSet.srcItemType.name ) ) ],
                          itemType                 = referenceSets.length === 1 ? referenceSets[ 0 ]?.[ referenceDirection === 'SrcToDest' ? 'destItemType' : 'srcItemType' ] : undefined,
                          itemTypeName             = itemType?.name ?? '',
                          // polymorphic ('*') binding with several target types: the candidate list is
                          // clustered by itemType and rendered non-paged with faked group header rows
                          groupByType              = referenceSets.length > 1;

                    let newSearchItems = [ ...searchItems ],
                        allLoaded      = true;

                    // check if result window has loaded before
                    for ( let i = first.current; i <= last.current, i < searchItems.length; i++ ) {
                        if ( searchItems[ i ] === undefined || searchItems[ i ] === null ) {
                            allLoaded = false;
                            break;
                        }
                    }

                    if ( !allLoaded ) {
                        if ( getDataAction ) {
                            const getConfigData : GetDataConfig = {
                                      schema,
                                      itemTypeName,
                                      itemTypeNames,

                                      searchString: filter.current,
                                      orderBy,

                                      // use shown label for search
                                      searchTemplate: itemLabel
                                          ? __( itemLabel )
                                          : `\$\{item.${itemType?.displayAttribute}\}`,

                                      start: first.current,
                                      limit,

                                      filterItems: ( items : Item[] ) => {
                                          const filteredItems = ( [] as Item[] ).concat( items );

                                          if ( filter.current ) {
                                              for ( let i = filteredItems.length - 1; i >= 0; i-- ) {
                                                  let labelForItem : string;

                                                  if ( itemLabel ) {
                                                      let resolvedReferenceSet : ReferenceSet | undefined;

                                                      if ( referenceSets.length === 1 ) {
                                                          resolvedReferenceSet = referenceSets[ 0 ];
                                                      }
                                                      else if ( referenceSets.length ) {
                                                          resolvedReferenceSet = referenceSets[ targetItemTypeIds.indexOf( filteredItems[ i ].itemTypeId ) ];
                                                      }

                                                      const messageResolverForItem = messageUtils.getMessageTemplateStringResolver( filteredItems[ i ], resolvedReferenceSet, messagePlaceholders );

                                                      labelForItem = messageResolverForItem( __( itemLabel, messagePlaceholders ) );
                                                  }
                                                  else {
                                                      labelForItem = filteredItems[ i ].displayAttributeValue;

                                                      if ( !labelForItem ) {
                                                          labelForItem = filteredItems[ i ].itemType.displayAttribute === 'key'
                                                              ? filteredItems[ i ].name
                                                              : filteredItems[ i ].key;
                                                      }
                                                  }

                                                  labelForItem = labelForItem.toLowerCase();

                                                  if ( labelForItem.indexOf( filter.current ) === -1 ) {
                                                      filteredItems.splice( i, 1 );
                                                  }
                                              }
                                          }

                                          return filteredItems;
                                      },
                                      orderItems : ( items : Item[] ) => {
                                          const orderedItems                   = ( [] as Item[] ).concat( items ),
                                                orderByDefinitions : OrderBy[] = [];

                                          if ( orderBy ) {
                                              const orderByParts = orderBy.split( ',' );

                                              for ( const orderByPart of orderByParts ) {
                                                  const orderByPartParts = ( orderByPart.trim().replaceAll( /\s+/g, ' ' ) ).split( ' ' );

                                                  if ( orderByPartParts.length && orderByPartParts.length < 3 ) {
                                                      const direction = ( orderByPartParts[ 1 ] ?? 'asc' ).toLowerCase();

                                                      if ( direction === 'asc' || direction === 'desc' ) {
                                                          orderByDefinitions.push( {
                                                              key: orderByPartParts[ 0 ],
                                                              direction
                                                          } );
                                                      }
                                                  }
                                              }
                                          }

                                          // sort by order by
                                          if ( orderByDefinitions.length ) {
                                              const sortFnc = ( item1 : Item, item2 : Item, orderByDefinition : OrderBy ) : -1 | 0 | 1 => {
                                                  let orderValue1 = messageUtils.getMessageTemplateStringResolver( item1 )( `\${${orderByDefinition.key}}`, true ) ?? '',
                                                      orderValue2 = messageUtils.getMessageTemplateStringResolver( item2 )( `\${${orderByDefinition.key}}`, true ) ?? '';

                                                  // order strings case independent
                                                  if ( orderValue1 && typeof orderValue1 === 'string' ) {
                                                      orderValue1 = orderValue1.toLowerCase();
                                                  }
                                                  if ( orderValue2 && typeof orderValue2 === 'string' ) {
                                                      orderValue2 = orderValue2.toLowerCase();
                                                  }

                                                  if ( orderValue1 > orderValue2 ) {
                                                      return orderByDefinition.direction === 'asc' ? 1 : -1;
                                                  }
                                                  else if ( orderValue1 < orderValue2 ) {
                                                      return orderByDefinition.direction === 'asc' ? -1 : 1;
                                                  }
                                                  else {
                                                      return 0;
                                                  }
                                              };

                                              orderedItems.sort( ( item1, item2 ) => {
                                                  let innerRetVal : number,
                                                      orderByCount : number = 0;

                                                  do {
                                                      innerRetVal = sortFnc( item1, item2, orderByDefinitions[ orderByCount++ ] );
                                                  } while ( innerRetVal === 0 && orderByCount < orderByDefinitions.length )

                                                  return innerRetVal;
                                              } );
                                          }
                                          // sort by label
                                          else {
                                              const sortFnc = ( item1 : Item, item2 : Item ) => {
                                                  let labelForItem1 : string = '',
                                                      labelForItem2 : string = '';

                                                  if ( itemLabel ) {
                                                      let resolvedReferenceSet : ReferenceSet | undefined;

                                                      if ( referenceSets.length === 1 ) {
                                                          resolvedReferenceSet = referenceSets[ 0 ];
                                                      }
                                                      else if ( referenceSets.length ) {
                                                          resolvedReferenceSet = referenceSets[ targetItemTypeIds.indexOf( item1.itemTypeId ) ];
                                                      }

                                                      let messageResolverForItem = messageUtils.getMessageTemplateStringResolver( item1, resolvedReferenceSet, messagePlaceholders );
                                                      labelForItem1 = messageResolverForItem( __( itemLabel, messagePlaceholders ) );

                                                      if ( referenceSets.length === 1 ) {
                                                          resolvedReferenceSet = referenceSets[ 0 ];
                                                      }
                                                      else if ( referenceSets.length ) {
                                                          resolvedReferenceSet = referenceSets[ targetItemTypeIds.indexOf( item2.itemTypeId ) ];
                                                      }

                                                      messageResolverForItem = messageUtils.getMessageTemplateStringResolver( item2, resolvedReferenceSet, messagePlaceholders );
                                                      labelForItem2 = messageResolverForItem( __( itemLabel, messagePlaceholders ) );
                                                  }
                                                  else {
                                                      labelForItem1 = item1.displayAttributeValue;

                                                      if ( !labelForItem1 ) {
                                                          labelForItem1 = item1.itemType.displayAttribute === 'key'
                                                              ? item1.name
                                                              : item1.key;
                                                      }

                                                      labelForItem2 = item2.displayAttributeValue;

                                                      if ( !labelForItem2 ) {
                                                          labelForItem2 = item2.itemType.displayAttribute === 'key'
                                                              ? item2.name
                                                              : item2.key;
                                                      }
                                                  }

                                                  labelForItem1 = labelForItem1.toLowerCase();
                                                  labelForItem2 = labelForItem2.toLowerCase();

                                                  if ( labelForItem1 > labelForItem2 ) {
                                                      return 1;
                                                  }
                                                  else if ( labelForItem1 < labelForItem2 ) {
                                                      return -1;
                                                  }
                                                  else {
                                                      return 0;
                                                  }
                                              };

                                              orderedItems.sort( sortFnc );
                                          }

                                          // polymorphic: cluster the (already label-sorted, hence stable)
                                          // items by their itemType label so each group is contiguous
                                          if ( groupByType ) {
                                              orderedItems.sort( ( item1, item2 ) => ( item1.itemType.label ?? '' ).toLowerCase().localeCompare( ( item2.itemType.label ?? '' ).toLowerCase() ) );
                                          }

                                          return orderedItems;
                                      },
                                      pageItems  : ( items : Item[] ) => {
                                          const pagedItems = ( [] as Item[] ).concat( items );

                                          // polymorphic: keep the full clustered list (headers are injected
                                          // client-side, non-paged, to avoid per-page header/total handling)
                                          if ( groupByType ) {
                                              return pagedItems;
                                          }

                                          return pagedItems.splice( first.current, limit );
                                      }
                                  },
                                  doLoad                        = async () => {
                                      setSearchItemsLoading( true );

                                      const data = await onGetData?.( getConfigData );

                                      if ( data?.items ) {
                                          const insertSearchItems : TransformedValue[ ] = [];

                                          let insertCount : number;

                                          if ( !groupByType ) {
                                              if ( newSearchItems.length > data.total ) {
                                                  newSearchItems.splice( data.total );
                                              }
                                              else if ( newSearchItems.length < data.total ) {
                                                  newSearchItems = newSearchItems.concat( Array.from( { length: data.total - newSearchItems.length } ) as TransformedValue[] );
                                              }
                                          }

                                          // add items
                                          for ( insertCount = 0; insertCount < data.items.length; insertCount++ ) {
                                              const item = data.items[ insertCount ];

                                              // inject a faked group header row when a new itemType group starts
                                              if ( groupByType && ( insertCount === 0 || data.items[ insertCount - 1 ].itemTypeId !== item.itemTypeId ) ) {
                                                  insertSearchItems.push( {
                                                      key          : `group_${item.itemTypeId}`,
                                                      id           : 0,
                                                      statusId     : resolvedNewStatusId,
                                                      label        : '',
                                                      sortLabel    : '',
                                                      itemId       : -item.itemTypeId,
                                                      isGroupHeader: true,
                                                      groupLabel   : item.itemType.label
                                                  } );
                                              }

                                              let labelForItem : string;

                                              if ( itemLabel ) {
                                                  let resolvedReferenceSet : ReferenceSet | undefined;

                                                  if ( referenceSets.length === 1 ) {
                                                      resolvedReferenceSet = referenceSets[ 0 ];
                                                  }
                                                  else if ( referenceSets.length ) {
                                                      resolvedReferenceSet = referenceSets[ targetItemTypeIds.indexOf( item.itemTypeId ) ];
                                                  }

                                                  const messageResolverForItem = messageUtils.getMessageTemplateStringResolver( item, resolvedReferenceSet, messagePlaceholders );

                                                  labelForItem = messageResolverForItem( __( itemLabel, messagePlaceholders ) );
                                              }
                                              else {
                                                  labelForItem = item.displayAttributeValue;

                                                  if ( !labelForItem ) {
                                                      labelForItem = item.itemType.displayAttribute === 'key'
                                                          ? item.name
                                                          : item.key;
                                                  }
                                              }

                                              insertSearchItems.push( {
                                                  key        : `item_${insertCount}`,
                                                  id         : 0,
                                                  statusId   : resolvedNewStatusId,
                                                  label      : statusColoringRenderer(
                                                      parse( labelForItem ),
                                                      item.statusId,
                                                      schema,
                                                      `item_${elementId}_item_${insertCount}`,
                                                      `item_${elementId}_item_${insertCount}`
                                                  ),
                                                  searchLabel: statusColoringRenderer(
                                                      parse( markFilterSearchesInText( labelForItem, filter.current, true ) ),
                                                      item.statusId,
                                                      schema,
                                                      `item_${elementId}_item_${insertCount}`,
                                                      `item_${elementId}_item_${insertCount}`
                                                  ),
                                                  sortLabel  : labelForItem.toLowerCase(),
                                                  itemId     : item.id,
                                                  item
                                              } );
                                          }

                                          if ( groupByType ) {
                                              // non-paged: the built list (items + headers) is the full option list
                                              newSearchItems = insertSearchItems;
                                          }
                                          else {
                                              sortTransformedValue( insertSearchItems );
                                              newSearchItems.splice( first.current, insertCount, ...insertSearchItems );
                                          }
                                      }

                                      setSearchItemsLoading( false );

                                      setSearchItems( newSearchItems );
                                  };

                            if ( requestTimeoutId.current ) {
                                clearTimeout( requestTimeoutId.current );
                            }

                            requestTimeoutId.current = setTimeout(
                                () => {
                                    doLoad().catch( ( error ) => {
                                        console.error( error );
                                        setSearchItemsLoading( false );
                                    } );
                                },
                                autoCompleteDelay
                            );
                        }
                        else {
                            const params : LoadReferenceFieldItemsParameters = {
                                      schema,

                                      itemTypeName,
                                      itemTypeNames,
                                      searchString: filter.current,
                                      orderBy,

                                      // use configured CFG:Query for search
                                      query: query
                                          ? {
                                              id        : query.id,
                                              parameters: queryParameters
                                          }
                                          : undefined,

                                      // use shown label for search
                                      searchTemplate: itemLabel
                                          ? __( itemLabel )
                                          : `\$\{item.${itemType?.displayAttribute}\}`,

                                      start: presentation !== 'checkboxes' ? first.current : undefined,
                                      limit: presentation !== 'checkboxes' ? limit : undefined
                                  },
                                  doLoad                                     = async () => {
                                      setSearchItemsLoading( true );

                                      const response = await xhr.post(
                                          `${BASE_URL}/api/form/loadReferenceFieldItems`,
                                          params,
                                          undefined,
                                          uid,
                                          searchRequestUid
                                      );

                                      if ( response?.data ) {
                                          const responseData = response.data as LoadReferenceFieldItemsResult;

                                          if ( responseData.data ) {
                                              const insertSearchItems : TransformedValue[ ] = [],
                                                    items                                   = responseData?.data?.items ?? [];

                                              let insertCount : number;

                                              if ( newSearchItems.length > responseData.metaData.total ) {
                                                  newSearchItems.splice( responseData.metaData.total );
                                              }
                                              else if ( newSearchItems.length < responseData.metaData.total ) {
                                                  newSearchItems = newSearchItems.concat( Array.from( { length: responseData.metaData.total - newSearchItems.length } ) as TransformedValue[] );
                                              }

                                              // add items
                                              for ( insertCount = 0; insertCount < items.length; insertCount++ ) {
                                                  const item = new Item( items[ insertCount ], uid, schema, true );

                                                  let labelForItem : string;

                                                  if ( itemLabel ) {
                                                      let resolvedReferenceSet : ReferenceSet | undefined;

                                                      if ( referenceSets.length === 1 ) {
                                                          resolvedReferenceSet = referenceSets[ 0 ];
                                                      }
                                                      else if ( referenceSets.length ) {
                                                          resolvedReferenceSet = referenceSets[ targetItemTypeIds.indexOf( item.itemTypeId ) ];
                                                      }

                                                      const messageResolverForItem = messageUtils.getMessageTemplateStringResolver( item, resolvedReferenceSet, messagePlaceholders );

                                                      labelForItem = messageResolverForItem( __( itemLabel, messagePlaceholders ) );
                                                  }
                                                  else {
                                                      labelForItem = item.displayAttributeValue;

                                                      if ( !labelForItem ) {
                                                          labelForItem = item.itemType.displayAttribute === 'key'
                                                              ? item.name
                                                              : item.key;
                                                      }
                                                  }

                                                  insertSearchItems.push( {
                                                      key        : `item_${insertCount}`,
                                                      id         : 0,
                                                      statusId   : resolvedNewStatusId,
                                                      label      : statusColoringRenderer(
                                                          !isInvalidHTML( labelForItem )
                                                              ? parse( labelForItem )
                                                              : labelForItem,
                                                          item.statusId,
                                                          schema,
                                                          `item_${elementId}_item_${insertCount}`,
                                                          `item_${elementId}_item_${insertCount}`
                                                      ),
                                                      searchLabel: statusColoringRenderer(
                                                          !isInvalidHTML( labelForItem )
                                                              ? parse( markFilterSearchesInText( labelForItem, filter.current, true ) )
                                                              : labelForItem,
                                                          item.statusId,
                                                          schema,
                                                          `item_${elementId}_item_${insertCount}`,
                                                          `item_${elementId}_item_${insertCount}`
                                                      ),
                                                      sortLabel  : labelForItem.toLowerCase(),
                                                      itemId     : item.id,
                                                      item
                                                  } );
                                              }

                                              sortTransformedValue( insertSearchItems );

                                              if ( presentation !== 'checkboxes' ) {
                                                  newSearchItems.splice( first.current, insertCount, ...insertSearchItems );
                                              }
                                              else {
                                                  newSearchItems = insertSearchItems;
                                                  first.current = 0;
                                                  last.current = insertCount;
                                              }
                                          }

                                          setSearchItemsLoading( false );
                                      }

                                      setSearchItems( newSearchItems );
                                  };

                            // abort running load
                            xhr.abort( uid, searchRequestUid );

                            if ( requestTimeoutId.current ) {
                                clearTimeout( requestTimeoutId.current );
                            }

                            requestTimeoutId.current = setTimeout(
                                () => {
                                    doLoad().catch( ( error ) => {
                                        console.error( error );
                                        setSearchItemsLoading( false );
                                    } );
                                },
                                autoCompleteDelay
                            );
                        }
                    }
                    else {
                        setSearchItems( newSearchItems );
                    }

                    setLoad( false );
                }
            },
            [
                filter.current,
                first.current,
                itemLabel,
                last.current,
                load,
                JSON.stringify( messagePlaceholders ),
                presentation,
                query?.id ?? 0,
                JSON.stringify( queryParameters ),
                referenceSets,
                resolvedNewStatusId,
                schema,
                targetItemTypeIds,
                user
            ]
        );

        useEffect(
            () => {
                setSearchItems( Array.from( { length: 101 } ) as TransformedValue[] );
            },
            [
                JSON.stringify( queryParameters )
            ]
        );

        return <FieldWrapper
            {..._.pick( props, fieldWrapperComponentProps ) as FieldWrapperComponentProps}

            key={`${elementId}_fieldWrapper`}

            uid={elementId}

            isDirty={markDirty && ( JSON.stringify( value ) !== JSON.stringify( originalValue ) || elementValue.map( eV => eV.key ).sort().join( ',' ) !== transformedValue.map( tV => tV.key ).sort().join( ',' ) )}
            messageTemplateStringResolver={messageResolver}
            tooltipDisabled={tooltipDisabled || overlay}

            autoComplete={false}
            pt={pt?.fieldWrapper}
        >
            {fieldContent}
        </FieldWrapper>;
    }
};

ReferenceField.defaultProps = defaultProps;

export default ReferenceField;
